<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useDateField, formatDocDate } from '@/shared/composables/dateUtils';
import { formatDateToDDMMYYYY } from '@/shared/composables/useDatePicker';
import { useAddressFormatter } from '@/shared/composables/useAddressFormatter';
import { useEntityDocVariables } from '@/shared/composables/useEntityDocVariables';
import { useOfficerAppointmentStore } from '../stores/officerAppointmentStore';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { entityApi } from '@/features/Entity/api/entityApi';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import { getOfficersByEntity, getAllotmentNextCertificateNo } from '../api/officerAppointmentApi';
import { getControllersByEntity, getControllerDetail } from '../api/controllerCessationApi';
import ContactListPage from '@/features/Contact/views/ContactListPage.vue';
import ContactDetailPage from '@/features/Contact/views/ContactDetailPage.vue';
import TaskFlowSection from './TaskFlowSection.vue';
import type { TaskConfig } from '../api/taskConfigApi';

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// 주소 포맷팅 함수
const { formatAddress } = useAddressFormatter();

// Entity doc_variables 조회
const { getEntityData } = useEntityDocVariables();

// Props: CreateTaskForm에서 선택한 taskConfig 전달받기
interface Props {
  taskConfig?: TaskConfig | null;
  entityId?: string;
  initialData?: any | null; // E-Filing 페이지에서 TASK_DATA 전달용
  readOnly?: boolean; // 읽기 전용 모드 (E-Filing 검증용)
  showTaskFlow?: boolean; // Task Flow 섹션 표시 여부 (E-Filing에서는 false)
  eFilingMode?: boolean; // E-Filing 모드
  validationErrors?: Record<string, string>; // 필드별 validation 에러 메시지
}

const props = withDefaults(defineProps<Props>(), {
  taskConfig: null,
  entityId: undefined,
  initialData: null,
  readOnly: false,
  showTaskFlow: true,
  eFilingMode: false,
  validationErrors: () => ({})
});

// Store
const officerAppointmentStore = useOfficerAppointmentStore();
const sessionStore = useSessionStore();

// Emits
const emit = defineEmits<{
  'auto-select-templates': [config: { categoryName: string; templateIds: string[] }],
  'filter-select-templates': [config: { categoryName: string; templateIds: string[]; filterValue: string }],
  'reset-templates': [],
  'update:taskFlow': [value: { all: boolean; createDocument: boolean; requestTSign: boolean; proceedWithEFiling: boolean }]
}>();

// 공통 날짜 필드 composable 사용 (초기값: 오늘 날짜)
const {
  dateValue: transactionDateValue,
  dateDisplay: transactionDate,
  datePickerValue: transactionDatePickerValue,
  datePlaceholder,
  datePickerLocale,
  firstDayOfWeek,
  dateMenu: transactionDateMenu
} = useDateField(new Date().toISOString().slice(0, 10));

// ========== Task Flow ==========
const taskFlow = ref({
  all: false,
  createDocument: false,
  requestTSign: false,
  proceedWithEFiling: false
});

const isCreateDocumentSupported = computed(() => {
  const steps = props.taskConfig?.processSteps;
  if (!steps || !Array.isArray(steps)) return false;
  return steps.some(s => s.toLowerCase().includes('doc'));
});

const isRequestTSignSupported = computed(() => {
  const steps = props.taskConfig?.processSteps;
  if (!steps || !Array.isArray(steps)) return false;
  return steps.some(s => s.toLowerCase().includes('sign'));
});

const isProceedWithEFilingSupported = computed(() => {
  const steps = props.taskConfig?.processSteps;
  if (!steps || !Array.isArray(steps)) return false;
  return steps.some(s => s.toLowerCase().includes('filing'));
});

const isAllSupported = computed(() =>
  isCreateDocumentSupported.value &&
  isRequestTSignSupported.value &&
  isProceedWithEFilingSupported.value
);

const updateTaskFlowState = () => {
  taskFlow.value.createDocument = isCreateDocumentSupported.value;
  taskFlow.value.requestTSign = isRequestTSignSupported.value;
  taskFlow.value.proceedWithEFiling = isProceedWithEFilingSupported.value;
  taskFlow.value.all = isAllSupported.value;
};

watch(() => props.taskConfig, updateTaskFlowState, { immediate: true });
watch(taskFlow, (v) => emit('update:taskFlow', { ...v }), { deep: true });

// Currency 섹션 인터페이스
interface SubClass {
  id: string;
  name: string;
  numberOfShares: string;
}

interface TreasuryShare {
  id: string;
  // Treasury share fields (필요시 추가)
}

interface ShareClass {
  id: string;
  classOfShares: string;
  numberOfShares: string;
  issuedShareCapital: string;
  paidUpShareCapital: string;
  subClasses: SubClass[];
  treasuryShares: TreasuryShare[];
  showTreasuryMessage?: boolean;
}

interface CurrencySection {
  id: string;
  currency: string;
  sharesPayable: string;
  shareClasses: ShareClass[];
}

interface ShareholderSection {
  id: string;
  shareholder: string;
  currencySections: CurrencySection[];
}

// Form fields
const entity = ref('');
const shareholderSections = ref<ShareholderSection[]>([
  {
    id: '1',
    shareholder: '',
    currencySections: [
      {
        id: '1',
        currency: '',
        sharesPayable: 'all-in-cash',
        shareClasses: [
          {
            id: `class-${Date.now()}`,
            classOfShares: '',
            numberOfShares: '',
            issuedShareCapital: '',
            paidUpShareCapital: '',
            subClasses: [],
            treasuryShares: []
          }
        ]
      }
    ]
  }
]);

// Options
const shareholderOptions = ref<Array<{ title: string; value: string }>>([]);
const classOfShareOptions = ref<Array<{ title: string; value: string }>>([]);
const currencyOptions = ref<Array<{ title: string; value: string; modalTitle?: string; bigo3?: string; bigo1?: string }>>([]);
const sharePayableOptions = ref<Array<{ title: string; value: string }>>([]);
const isLoadingShareholders = ref(false);

// Shareholder별 Contact 데이터 저장 (sectionId -> contactData)
const shareholderContactData = ref<Map<string, any>>(new Map());

// BaseCode 옵션 데이터 (Contact 정보 표시용)
const identificationTypeOptions = ref<Array<{ title: string; value: string; bigo1?: string }>>([]);
const nationalityOptions = ref<Array<{ title: string; value: string; bigo1?: string }>>([]);
const countryOptions = ref<Array<{ title: string; value: string; bigo3?: string }>>([]);
const addressTypeOptions = ref<Array<{ title: string; value: string }>>([]);

// Jurisdiction 코드명 매핑 (B15)
const jurisdictionCodeNameMap = ref<Map<string, string>>(new Map());

const placeholderLabel = computed(() => t('startTask.Please Select') || 'Please Select');

const currencyOptionItems = computed(() => {
  const options = currencyOptions.value;
  return options
    .filter((option) => option.value && option.value !== 'ALL')
    .map((option) => ({
      title: option.modalTitle || option.title || option.value,
      value: option.value,
      bigo3: option.bigo3,
      bigo1: option.bigo1
    }));
});

const sharePayableOptionItems = computed(() => {
  return [
    { title: 'All in cash', value: 'all-in-cash' },
    { title: 'All otherwise than in cash', value: 'all-otherwise' },
    { title: 'No consideration', value: 'no-consideration' },
    { title: 'Partially in cash and otherwise than in cash', value: 'partially' }
  ];
});

// Entity 정보를 위한 ref 추가
const priorApprovalFiled = ref<string>(''); // 'yes', 'no', or '' (초기값 없음)
const entityProfileData = ref<any>(null);
const isLoadingEntityProfile = ref(false);

// Entity 정보 가져오기
const getEntityInfo = computed(() => {
  if (!entity.value) return null;
  const selectedEntity = officerAppointmentStore.entities.find((ent: any) =>
    (ent.entityCd || ent.ENTITY_CD) === entity.value
  );
  return selectedEntity || null;
});

// Entity Type과 Primary Activity를 위한 옵션
const entityTypeOptions = ref<Array<{ title: string; value: string }>>([]);
const ssicOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);

// Certificate Details 및 RORC Information Setting 필드
const certificateType = ref('');
const certificateNo = ref('');
const chairPerson = ref('');
const representativeDirector = ref('');
const rorc = ref('');
const representative = ref('');

// 옵션들
const certificateTypeOptions = ref<Array<{ title: string; value: string }>>([]);
const chairPersonOptions = ref<Array<{ title: string; value: string }>>([]);
const representativeDirectorOptions = ref<Array<{ title: string; value: string }>>([]);
const rorcOptions = ref<Array<{ title: string; value: string }>>([]);
const representativeOptions = ref<Array<{ title: string; value: string }>>([]);

// 아코디언 확장/축소 상태 (Entity + Shareholders + Certificate Details + RORC)
const allExpandedPanels = ref<string[]>([]);

// Expand All / Collapse All 함수
const expandAll = () => {
  const panels = ['entity'];
  panels.push(...shareholderSections.value.map(s => s.id));
  panels.push('certificate-details', 'rorc-info');
  allExpandedPanels.value = panels;
};

const collapseAll = () => {
  allExpandedPanels.value = [];
};

// Shareholder 이름 가져오기
const getShareholderName = (shareholderCd: string): string => {
  if (!shareholderCd) return '';
  const option = shareholderOptions.value.find(opt => opt.value === shareholderCd);
  return option?.title || '';
};

// BaseCode 옵션 로드 함수 (ID Type, Nationality, Country, Address Type)
const loadBaseCodeOptions = async () => {
  try {
    // ID Type (B03)
    const idTypeResponse = await baseCodeApi.getCodesByMainCode('CM', 'B03', undefined);
    const idTypeCodes = (idTypeResponse as any).codes || idTypeResponse.data?.codes || [];
    identificationTypeOptions.value = idTypeCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo1: code.bigo1 || code.BIGO1 || ''
    })).filter((opt: any) => opt.title && opt.value);

    // Nationality (B04)
    const nationalityResponse = await baseCodeApi.getCodesByMainCode('CM', 'B04', undefined);
    const nationalityCodes = (nationalityResponse as any).codes || nationalityResponse.data?.codes || [];
    nationalityOptions.value = nationalityCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo1: code.bigo1 || code.BIGO1 || ''
    })).filter((opt: any) => opt.title && opt.value);

    // Country (B05)
    const countryResponse = await baseCodeApi.getCodesByMainCode('CM', 'B05', undefined);
    const countryCodes = (countryResponse as any).codes || countryResponse.data?.codes || [];
    countryOptions.value = countryCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo3: code.bigo3 || code.BIGO3 || ''
    })).filter((opt: any) => opt.title && opt.value);

    // Address Type (B07)
    const addressTypeResponse = await baseCodeApi.getCodesByMainCode('CM', 'B07', undefined);
    const addressTypeCodes = (addressTypeResponse as any).codes || addressTypeResponse.data?.codes || [];
    addressTypeOptions.value = addressTypeCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || ''
    })).filter((opt: any) => opt.title && opt.value);
  } catch (error: any) {
    console.error('❌ Failed to load BaseCode options:', error);
  }
};

// Shareholder 선택 시 Contact 정보 로드
const loadShareholderContactData = async (sectionId: string, contactCd: string) => {
  if (!contactCd) {
    shareholderContactData.value.delete(sectionId);
    return;
  }

  try {
    const response = await contactsApi.getContactById(contactCd);
    const contact = (response as any).contact || response.data?.contact;

    if (contact) {
      const isCorporate = contact.CATEGORY_CD === 'B09001';
      
      const baseData: any = {
        // 공통 필드
        CATEGORY_CD: contact.CATEGORY_CD || '',
        CONTACT_NAME: contact.CONTACT_NAME || contact.contactName || '',
        CONTACT_REG_NO: contact.CONTACT_REG_NO || contact.contactRegNo || '',
        // Contact 섹션
        COUNTRY_CD: contact.COUNTRY_CD || contact.countryCd || '',
        AREA_CODE: contact.AREA_CODE || contact.areaCode || '',
        MOBILE_NO: contact.MOBILE_NO || contact.mobileNo || '',
        CONTACT_EMAIL: contact.CONTACT_EMAIL || contact.contactEmail || ''
      };

      if (isCorporate) {
        // Corporate 필드
        Object.assign(baseData, {
          JURISDICTION: contact.JURISDICTION || contact.jurisdiction || '',
          DATE_OF_INCORPORATION: contact.DATE_OF_INCORPORATION || contact.dateOfIncorporation || contact.incorporationDate || '',
          REPRESENTATIVE_CD: contact.REPRESENTATIVE_CD || contact.representativeCd || contact.representativeName || '',
          REPRESENTATIVE_NAME: contact.REPRESENTATIVE_NAME || contact.representativeName || '',
          AUDITOR_YN: contact.AUDITOR_YN || contact.auditorYn || contact.publicAccountingEntities || 'No',
          LEGAL_FORM: contact.LEGAL_FORM || contact.legalForm || '',
          INCORPORATION_STATUTE: contact.INCORPORATION_STATUTE || contact.incorporationStatute || '',
          CORPORATE_ENTITY_REGISTER: contact.CORPORATE_ENTITY_REGISTER || contact.corporateEntityRegister || '',
          // Residential Address (Corporate도 주소 정보 있음)
          ADDRESS_TYPE_CD: contact.ADDRESS_TYPE_CD || contact.addressTypeCd || 'B07001',
          POSTAL_CODE: contact.POSTAL_CODE || contact.postalCode || '',
          BLOCK_NO: contact.BLOCK_NO || contact.blockNo || '',
          STREET_NAME: contact.STREET_NAME || contact.streetName || '',
          BUILDING_NAME: contact.BUILDING_NAME || contact.buildingName || '',
          ADDR_LEVEL: contact.ADDR_LEVEL || contact.addrLevel || '',
          ADDR_UNIT: contact.ADDR_UNIT || contact.addrUnit || '',
          FOREIGN_ADDRESS_LINE_1: contact.FOREIGN_ADDRESS_LINE_1 || contact.foreignAddressLine1 || '',
          FOREIGN_ADDRESS_LINE_2: contact.FOREIGN_ADDRESS_LINE_2 || contact.foreignAddressLine2 || '',
          // Contact Address (Alternate Address)
          ALTERNATE_ADDRESS_CD: contact.ALTERNATE_ADDRESS_CD || contact.alternateAddressCd || contact.ADDRESS_TYPE_CD || contact.addressTypeCd || 'B07001',
          ALT_POSTAL_CODE: contact.ALT_POSTAL_CODE || contact.altPostalCode || '',
          ALT_BLOCK_NO: contact.ALT_BLOCK_NO || contact.altBlockNo || '',
          ALT_STREET_NAME: contact.ALT_STREET_NAME || contact.altStreetName || '',
          ALT_BUILDING_NAME: contact.ALT_BUILDING_NAME || contact.altBuildingName || '',
          ALT_ADDR_LEVEL: contact.ALT_ADDR_LEVEL || contact.altAddrLevel || '',
          ALT_ADDR_UNIT: contact.ALT_ADDR_UNIT || contact.altAddrUnit || '',
          ALT_FOREIGN_ADDRESS_LINE_1: contact.ALT_FOREIGN_ADDRESS_LINE_1 || contact.altForeignAddressLine1 || '',
          ALT_FOREIGN_ADDRESS_LINE_2: contact.ALT_FOREIGN_ADDRESS_LINE_2 || contact.altForeignAddressLine2 || ''
        });
      } else {
        // Individual 필드
        Object.assign(baseData, {
          FIRST_NAME: contact.FIRST_NAME || contact.firstName || '',
          MIDDLE_NAME: contact.MIDDLE_NAME || contact.middleName || '',
          SURNAME: contact.SURNAME || contact.surname || '',
          ID_TYPE_CD: contact.ID_TYPE_CD || contact.idTypeCd || '',
          NATIONALITY_CD: contact.NATIONALITY_CD || contact.nationalityCd || '',
          DATE_OF_BIRTH: contact.DATE_OF_BIRTH || contact.dateOfBirth || '',
          // Residential Address
          ADDRESS_TYPE_CD: contact.ADDRESS_TYPE_CD || contact.addressTypeCd || 'B07001',
          POSTAL_CODE: contact.POSTAL_CODE || contact.postalCode || '',
          BLOCK_NO: contact.BLOCK_NO || contact.blockNo || '',
          STREET_NAME: contact.STREET_NAME || contact.streetName || '',
          BUILDING_NAME: contact.BUILDING_NAME || contact.buildingName || '',
          ADDR_LEVEL: contact.ADDR_LEVEL || contact.addrLevel || '',
          ADDR_UNIT: contact.ADDR_UNIT || contact.addrUnit || '',
          FOREIGN_ADDRESS_LINE_1: contact.FOREIGN_ADDRESS_LINE_1 || contact.foreignAddressLine1 || '',
          FOREIGN_ADDRESS_LINE_2: contact.FOREIGN_ADDRESS_LINE_2 || contact.foreignAddressLine2 || '',
          // Contact Address (Alternate Address)
          ALTERNATE_ADDRESS_CD: contact.ALTERNATE_ADDRESS_CD || contact.alternateAddressCd || contact.ADDRESS_TYPE_CD || contact.addressTypeCd || 'B07001',
          ALT_POSTAL_CODE: contact.ALT_POSTAL_CODE || contact.altPostalCode || '',
          ALT_BLOCK_NO: contact.ALT_BLOCK_NO || contact.altBlockNo || '',
          ALT_STREET_NAME: contact.ALT_STREET_NAME || contact.altStreetName || '',
          ALT_BUILDING_NAME: contact.ALT_BUILDING_NAME || contact.altBuildingName || '',
          ALT_ADDR_LEVEL: contact.ALT_ADDR_LEVEL || contact.altAddrLevel || '',
          ALT_ADDR_UNIT: contact.ALT_ADDR_UNIT || contact.altAddrUnit || '',
          ALT_FOREIGN_ADDRESS_LINE_1: contact.ALT_FOREIGN_ADDRESS_LINE_1 || contact.altForeignAddressLine1 || '',
          ALT_FOREIGN_ADDRESS_LINE_2: contact.ALT_FOREIGN_ADDRESS_LINE_2 || contact.altForeignAddressLine2 || ''
        });
      }

      shareholderContactData.value.set(sectionId, baseData);
      
      // Contact 정보 로드 완료 (문서 자동 선택은 watch에서 처리)
    }
  } catch (error: any) {
    console.error('❌ Failed to fetch contact details:', error);
    shareholderContactData.value.delete(sectionId);
  }
};

// Contact 정보 표시용 computed
const getContactData = (sectionId: string) => {
  return shareholderContactData.value.get(sectionId) || null;
};

// Date of Birth 표시용 포맷 (ISO 문자열 및 YYYY-MM-DD 형식 모두 처리)
const formatDateOfBirth = (dob: string): string => {
  return formatDateToDDMMYYYY(dob);
};

// Date of Incorporation 표시용 포맷
const formatDateOfIncorporation = (doi: string): string => {
  if (!doi) return '';
  const parts = doi.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return doi;
};

// Contact가 Corporate인지 확인
const isCorporateContact = (sectionId: string): boolean => {
  const contactData = getContactData(sectionId);
  return contactData?.CATEGORY_CD === 'B09001';
};

// Jurisdiction 코드명 로드 (B15)
const loadJurisdictionCodeNames = async () => {
  try {
    const response = await baseCodeApi.getCodesByMainCode('CM', 'B15', undefined);
    const codes = (response as any).codes || response.data?.codes || [];
    codes.forEach((code: any) => {
      const totalCode = String(code.totalCode || code.TOTAL_CODE || '').trim();
      const codeName = String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim();
      if (totalCode && codeName) {
        jurisdictionCodeNameMap.value.set(totalCode, codeName);
      }
    });
    console.log('✅ Jurisdiction Code Names loaded:', jurisdictionCodeNameMap.value);
  } catch (error) {
    console.error('❌ Failed to load Jurisdiction Code Names:', error);
  }
};

// Jurisdiction 표시용 (BaseCode 이름으로 표시)
const getJurisdictionDisplay = (code: string): string => {
  if (!code) return '';
  return jurisdictionCodeNameMap.value.get(code) || code;
};

// Representative Name 표시용 (간단히 코드 반환, 필요시 Contact 정보 로드 가능)
const getRepresentativeNameDisplay = (contactCd: string): string => {
  if (!contactCd) return '';
  // TODO: 필요시 contactsApi.getContactById로 로드하여 이름 표시
  return contactCd;
};

// Identification Type 표시용
const getIdentificationTypeDisplay = (code: string): string => {
  if (!code) return '';
  const option = identificationTypeOptions.value.find(opt => opt.value === code);
  return option?.title || code;
};

// Nationality 표시용
const getNationalityDisplay = (code: string): string => {
  if (!code) return '';
  const option = nationalityOptions.value.find(opt => opt.value === code);
  return option?.title || code;
};

// Country Code 표시용
const getCountryCodeDisplay = (code: string): string => {
  if (!code) return '';
  const option = countryOptions.value.find(opt => opt.value === code);
  return option?.title || code;
};

// Address Type 표시용
const getAddressTypeDisplay = (code: string): string => {
  if (!code) return '';
  const option = addressTypeOptions.value.find(opt => opt.value === code);
  return option?.title || code;
};

// Entity Type 옵션 로드
const loadEntityTypeOptions = async () => {
  try {
    const response = await entityApi.getEntityTypes();
    const responseData = (response as any).data || response;
    const codes = responseData.data?.codes || responseData.codes || [];
    
    entityTypeOptions.value = codes
      .map((code: any) => ({
        title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
        value: code.totalCode || code.TOTAL_CODE || ''
      }))
      .filter((item: any) => {
        return item.value && item.title && item.value.slice(-3) !== '000';
      });
  } catch (error) {
    console.error('Failed to load entity type options', error);
    entityTypeOptions.value = [];
  }
};

// SSIC 목록 로드 (Primary Activity용)
const loadSSICOptions = async () => {
  try {
    const response = await entityApi.getSSICList();
    const codes = (response as any).data?.codes || response.data?.data?.codes || [];
    
    ssicOptions.value = codes
      .map((code: any) => ({
        TOTAL_CODE: String(code.TOTAL_CODE || code.totalCode || '').trim(),
        CODE_NAME: String(code.CODE_NAME || code.codeName || '').trim()
      }))
      .filter((item: any) => item.TOTAL_CODE && item.CODE_NAME && item.TOTAL_CODE.length > 0 && item.CODE_NAME.length > 0);
  } catch (error) {
    console.error('Failed to load SSIC options', error);
    ssicOptions.value = [];
  }
};

// Entity 프로필 데이터 로드
const loadEntityProfile = async (entityId: string) => {
  if (!entityId) {
    entityProfileData.value = null;
    return;
  }
  
  try {
    isLoadingEntityProfile.value = true;
    const response = await entityApi.getEntityProfile(entityId);
    entityProfileData.value = (response as any).data || response;
  } catch (error) {
    console.error('Failed to load entity profile', error);
    entityProfileData.value = null;
  } finally {
    isLoadingEntityProfile.value = false;
  }
};

// Entity Type 이름 가져오기
const getEntityTypeName = (): string => {
  if (!entityProfileData.value) return '-';
  
  const entityTypeCode = entityProfileData.value?.registrySync?.entityType || 
                         entityProfileData.value?.data?.registrySync?.entityType || 
                         entityProfileData.value?.entityType || '';
  
  if (!entityTypeCode) return '-';
  
  const option = entityTypeOptions.value.find(opt => opt.value === entityTypeCode);
  return option?.title || entityTypeCode;
};

// Primary Activity 이름 가져오기
const getPrimaryActivityName = (): string => {
  if (!entityProfileData.value) return '-';
  
  const primaryActivityCode = entityProfileData.value?.registeredAddress?.primaryActivityCode || 
                             entityProfileData.value?.data?.registeredAddress?.primaryActivityCode || 
                             entityProfileData.value?.registeredAddress?.primaryActivity || '';
  
  if (!primaryActivityCode) return '-';
  
  const option = ssicOptions.value.find(opt => opt.TOTAL_CODE === primaryActivityCode);
  return option?.CODE_NAME || primaryActivityCode;
};

// 이미 자동 선택 이벤트를 발생시켰는지 추적
let hasEmittedAutoSelect = false;

// Shareholder별로 이미 문서 선택 이벤트를 발생시켰는지 추적 (복수 Shareholder 지원)
const hasEmittedAutoSelectByCategory = ref<Map<string, Set<string>>>(new Map()); // Shareholder ID -> Category Set

// 카테고리/파일 자동 선택 함수 (초기 로드 시 전체 문서 선택)
const autoSelectCategoryAndFiles = () => {
  const taskConfig = props.taskConfig;

  if (!taskConfig) {
    console.warn('⚠️ Task Config not available. Please select a Task Type first.');
    return;
  }

  if (hasEmittedAutoSelect) {
    console.log('⏭️ Auto-select already emitted for this config, skipping...');
    return;
  }

  try {
    if (taskConfig.documents && Array.isArray(taskConfig.documents)) {
      // FILTER가 없는 문서만 선택 (초기 로드용)
      const documentsWithoutFilter = taskConfig.documents.filter(
        (doc: any) => !doc.filter || doc.filter === ''
      );

      if (documentsWithoutFilter.length > 0) {
        const templateIds = documentsWithoutFilter
          .map((doc: any) => doc.TMP_ID || doc.DOC_TYPE)
          .filter((id: any): id is string | number => id != null)
          .map((id: any) => String(id));

        const categoryName = documentsWithoutFilter[0]?.category?.trim() || null;

        console.log('📋 Auto-selecting category and templates (no filter):', {
          categoryName,
          templateIds,
          templateCount: templateIds.length
        });

        emit('auto-select-templates', {
          categoryName: categoryName || '',
          templateIds
        });
      }

      hasEmittedAutoSelect = true;
    }
  } catch (error) {
    console.error('❌ Failed to auto-select templates:', error);
  }
};

// 모든 Shareholder를 확인하고 필요한 문서 자동 선택 함수 (복수 Shareholder 지원)
const autoSelectTemplatesByShareholders = async () => {
  const taskConfig = props.taskConfig;

  if (!taskConfig) {
    console.warn('⚠️ Task Config not available. Please select a Task Type first.');
    return;
  }

  try {
    // 모든 Shareholder의 CATEGORY_CD 수집
    const categoryCounts = {
      individual: 0, // B09002
      corporate: 0   // B09001
    };

    // Shareholder가 선택된 섹션들을 확인
    const selectedShareholders: Array<{ sectionId: string; contactCd: string }> = [];
    for (const section of shareholderSections.value) {
      if (section.shareholder) {
        selectedShareholders.push({
          sectionId: section.id,
          contactCd: section.shareholder
        });
      }
    }

    // 각 Shareholder의 CATEGORY_CD 조회
    for (const { sectionId, contactCd } of selectedShareholders) {
      // 이미 로드된 Contact 정보가 있으면 사용
      let contactData = shareholderContactData.value.get(sectionId);
      
      // Contact 정보가 없으면 API로 조회
      if (!contactData) {
        try {
          const response = await contactsApi.getContactById(contactCd);
          const contact = (response as any).contact || response.data?.contact;
          if (contact) {
            const categoryCd = contact.CATEGORY_CD || contact.categoryCd || '';
            if (categoryCd === 'B09001') {
              categoryCounts.corporate++;
            } else if (categoryCd === 'B09002') {
              categoryCounts.individual++;
            }
          }
        } catch (error: any) {
          console.warn(`⚠️ Failed to fetch contact ${contactCd}:`, error);
        }
      } else {
        // 이미 로드된 Contact 정보 사용
        const categoryCd = contactData.CATEGORY_CD || '';
        if (categoryCd === 'B09001') {
          categoryCounts.corporate++;
        } else if (categoryCd === 'B09002') {
          categoryCounts.individual++;
        }
      }
    }

    console.log('📊 Shareholder Category Counts:', categoryCounts);

    // 문서 선택
    if (taskConfig.documents && Array.isArray(taskConfig.documents)) {
      // 1. 필터 없는 문서는 항상 선택
      const documentsWithoutFilter = taskConfig.documents.filter(
        (doc: any) => !doc.filter || doc.filter === ''
      );
      
      // 2. 개인이 1명 이상이면 → 필터가 'B09002'인 문서 선택
      const individualDocuments = categoryCounts.individual > 0 
        ? taskConfig.documents.filter((doc: any) => doc.filter === 'B09002')
        : [];

      // 3. 법인이 1명 이상이면 → 필터가 'B09001'인 문서 선택
      const corporateDocuments = categoryCounts.corporate > 0
        ? taskConfig.documents.filter((doc: any) => doc.filter === 'B09001')
        : [];

      // 모든 선택할 문서 ID 수집 (필터 없는 문서 + 필터 걸린 문서)
      const allTemplateIds: string[] = [];
      
      // 필터 없는 문서 ID 추가
      if (documentsWithoutFilter.length > 0) {
        const noFilterTemplateIds = documentsWithoutFilter
          .map((doc: any) => doc.DOC_TYPE || doc.TMP_ID)
          .filter((id: any) => id != null)
          .map((id: any) => String(id));
        allTemplateIds.push(...noFilterTemplateIds);
      }

      // 개인 문서 ID 추가
      if (individualDocuments.length > 0) {
        const individualTemplateIds = individualDocuments
          .map((doc: any) => doc.DOC_TYPE || doc.TMP_ID)
          .filter((id: any) => id != null)
          .map((id: any) => String(id));
        allTemplateIds.push(...individualTemplateIds);
      }

      // 법인 문서 ID 추가
      if (corporateDocuments.length > 0) {
        const corporateTemplateIds = corporateDocuments
          .map((doc: any) => doc.DOC_TYPE || doc.TMP_ID)
          .filter((id: any) => id != null)
          .map((id: any) => String(id));
        allTemplateIds.push(...corporateTemplateIds);
      }

      // 중복 제거
      const uniqueTemplateIds = Array.from(new Set(allTemplateIds));

      console.log('📋 Shareholder 기반 문서 선택:', {
        individualShareholderCount: categoryCounts.individual,
        corporateShareholderCount: categoryCounts.corporate,
        noFilterCount: documentsWithoutFilter.length,
        individualDocumentCount: individualDocuments.length,
        corporateDocumentCount: corporateDocuments.length,
        totalSelected: uniqueTemplateIds.length,
        templateIds: uniqueTemplateIds
      });

      // 필터 없는 문서 ID 추출
      const noFilterTemplateIds = documentsWithoutFilter.length > 0
        ? documentsWithoutFilter
            .map((doc: any) => doc.DOC_TYPE || doc.TMP_ID)
            .filter((id: any) => id != null)
            .map((id: any) => String(id))
        : [];

      // 개인 문서 ID 추출
      const individualTemplateIds = individualDocuments.length > 0
        ? individualDocuments
            .map((doc: any) => doc.DOC_TYPE || doc.TMP_ID)
            .filter((id: any) => id != null)
            .map((id: any) => String(id))
        : [];

      // 법인 문서 ID 추출
      const corporateTemplateIds = corporateDocuments.length > 0
        ? corporateDocuments
            .map((doc: any) => doc.DOC_TYPE || doc.TMP_ID)
            .filter((id: any) => id != null)
            .map((id: any) => String(id))
        : [];

      // 필터 걸린 문서가 있으면 filter-select-templates로 emit (필터 없는 문서 포함)
      // filter-select-templates는 resetTemplates를 호출하므로, 필터 없는 문서도 함께 포함해야 함
      if (individualTemplateIds.length > 0 || corporateTemplateIds.length > 0) {
        // 모든 선택할 문서 ID (필터 없는 + 필터 걸린)
        const allSelectedIds = Array.from(new Set([
          ...noFilterTemplateIds,
          ...individualTemplateIds,
          ...corporateTemplateIds
        ]));

        // 개인 문서가 있으면 개인 문서로 emit
        if (individualTemplateIds.length > 0) {
          const categoryName = individualDocuments[0]?.category?.trim() || '';
          
          console.log('📋 개인 Shareholder 문서 선택 (필터 없는 문서 포함):', {
            noFilterCount: noFilterTemplateIds.length,
            individualCount: individualTemplateIds.length,
            totalSelected: allSelectedIds.length,
            templateIds: allSelectedIds
          });

          // 필터 없는 문서 + 개인 문서 모두 포함하여 선택
          emit('filter-select-templates', {
            categoryName: categoryName || '',
            templateIds: allSelectedIds, // 필터 없는 문서 + 개인 문서 모두 포함
            filterValue: 'B09002'
          });
        }

        // 법인 문서가 있으면 법인 문서로 emit (개인 문서와 함께 있을 수 있음)
        if (corporateTemplateIds.length > 0) {
          const categoryName = corporateDocuments[0]?.category?.trim() || '';
          
          console.log('📋 법인 Shareholder 문서 선택 (필터 없는 문서 포함):', {
            noFilterCount: noFilterTemplateIds.length,
            corporateCount: corporateTemplateIds.length,
            totalSelected: allSelectedIds.length,
            templateIds: allSelectedIds
          });

          // 필터 없는 문서 + 법인 문서 모두 포함하여 선택 (개인 문서가 있으면 이미 emit했지만, 법인만 있을 때를 위해)
          if (individualTemplateIds.length === 0) {
            emit('filter-select-templates', {
              categoryName: categoryName || '',
              templateIds: allSelectedIds, // 필터 없는 문서 + 법인 문서 모두 포함
              filterValue: 'B09001'
            });
          }
        }
      } else {
        // 필터 걸린 문서가 없으면 필터 없는 문서만 선택 (auto-select-templates로)
        if (noFilterTemplateIds.length > 0) {
          const categoryName = documentsWithoutFilter[0]?.category?.trim() || '';
          
          console.log('📋 필터 없는 문서만 선택:', {
            templateIds: noFilterTemplateIds,
            count: noFilterTemplateIds.length
          });

          emit('auto-select-templates', {
            categoryName: categoryName || '',
            templateIds: noFilterTemplateIds
          });
        }
      }

      console.log('📋 [SharesAllotmentForm] Shareholder 기반 문서 자동 선택 완료:', {
        individualCount: categoryCounts.individual,
        corporateCount: categoryCounts.corporate,
        noFilterDocuments: documentsWithoutFilter.length
      });
    }
  } catch (error) {
    console.error('❌ Failed to auto-select templates by shareholders:', error);
  }
};

// Shareholder의 Category에 따라 문서 자동 선택 함수 (단일 Shareholder용 - 하위 호환성 유지)
const autoSelectTemplatesByCategory = (categoryCd: string, sectionId: string) => {
  // 새로운 로직으로 대체: 모든 Shareholder를 확인하고 선택
  autoSelectTemplatesByShareholders();
};

// taskConfig가 변경될 때 카테고리/파일 자동 선택
watch(() => props.taskConfig, (newConfig, oldConfig) => {
  if (newConfig) {
    if (newConfig !== oldConfig) {
      hasEmittedAutoSelect = false;
    }
    console.log('📌 TaskConfig changed, auto-selecting category/files');
    autoSelectCategoryAndFiles();
  }
}, { immediate: true });

// Shareholder 섹션 초기 상태 생성 함수
const createInitialShareholderSection = () => {
  return [
    {
      id: '1',
      shareholder: '',
      currencySections: [
        {
          id: '1',
          currency: '',
          sharesPayable: 'all-in-cash',
          shareClasses: [
            {
              id: `class-${Date.now()}`,
              classOfShares: '',
              numberOfShares: '',
              issuedShareCapital: '',
              paidUpShareCapital: '',
              subClasses: [],
              treasuryShares: []
            }
          ]
        }
      ]
    }
  ];
};

// 데이터 복원 중 플래그 (setFormData에서 사용)
const isRestoringData = ref(false);

// Entity 변경 시 로깅 및 shareholder 목록 로드
watch(entity, async (newEntityCd, oldEntityCd) => {
  console.log('📌 Entity changed:', newEntityCd);
  
  // 데이터 복원 중이면 watch 실행 안 함
  if (isRestoringData.value) {
    console.log('📋 [SharesAllotmentForm] 데이터 복원 중 - Entity watch 건너뜀');
    return;
  }
  
  // Entity가 변경되면 Shareholder 섹션을 초기 상태로 되돌리기
  if (oldEntityCd && newEntityCd !== oldEntityCd) {
    shareholderSections.value = createInitialShareholderSection();
    // Contact 데이터도 초기화
    shareholderContactData.value.clear();
    // 문서 자동 선택 추적도 초기화
    hasEmittedAutoSelectByCategory.value.clear();
  }
  
  if (newEntityCd) {
    // Entity 섹션 즉시 펼치기 (데이터 로드 전에)
    allExpandedPanels.value = ['entity'];
    
    // 데이터 로드는 비동기로 처리
    Promise.all([
      loadShareholderContacts(newEntityCd),
      loadEntityProfile(newEntityCd),
      loadChairPersonOptions(newEntityCd),
      loadCertificateTypes(newEntityCd),
      loadNextCertificateNo(newEntityCd),
      loadRepresentativeDirectorOptions(newEntityCd),
      loadRorcOptions(newEntityCd)
    ]).catch(error => {
      console.error('Failed to load entity data:', error);
    });
  } else {
    shareholderOptions.value = [];
    entityProfileData.value = null;
    certificateTypeOptions.value = [];
    certificateNo.value = '';
    chairPersonOptions.value = [];
    representativeDirectorOptions.value = [];
    rorcOptions.value = [];
    representativeOptions.value = [];
    allExpandedPanels.value = [];
    // Entity가 비어있을 때도 Shareholder 섹션 초기화
    shareholderSections.value = createInitialShareholderSection();
    shareholderContactData.value.clear();
  }
});

// RORC 변경 시 REPRESENTATIVE 옵션 로드
watch(rorc, async (newRorcId) => {
  if (newRorcId) {
    await loadRepresentativeOptions(newRorcId);
  } else {
    representativeOptions.value = [];
    representative.value = '';
  }
});

// Shareholder 선택 시 Contact 정보 로드 및 문서 자동 선택 (각 section별로 watch)
watch(
  () => shareholderSections.value.map(s => s.shareholder),
  async (newShareholders, oldShareholders) => {
    // 데이터 복원 중이면 watch 실행 안 함
    if (isRestoringData.value) {
      console.log('📋 [SharesAllotmentForm] 데이터 복원 중 - Shareholder watch 건너뜀');
      return;
    }
    
    // Shareholder 변경 여부 확인
    let hasChanges = false;
    
    for (let index = 0; index < shareholderSections.value.length; index++) {
      const section = shareholderSections.value[index];
      const newShareholder = newShareholders[index];
      const oldShareholder = oldShareholders?.[index];
      
      if (newShareholder && newShareholder !== oldShareholder) {
        // Shareholder 선택 시 Contact 정보 로드
        await loadShareholderContactData(section.id, newShareholder);
        hasChanges = true;
      } else if (!newShareholder && oldShareholder) {
        // Shareholder 선택 해제
        shareholderContactData.value.delete(section.id);
        hasEmittedAutoSelectByCategory.value.delete(section.id);
        hasChanges = true;
      }
    }
    
    // Shareholder가 변경되면 모든 Shareholder를 다시 확인하고 문서 자동 선택
    if (hasChanges) {
      // 약간의 지연을 두어 모든 Contact 정보가 로드될 시간 확보
      await new Promise(resolve => setTimeout(resolve, 100));
      await autoSelectTemplatesByShareholders();
    }
  },
  { deep: true }
);

// props.entityId 변경 시 entity ref 동기화 및 shareholder 목록 로드
watch(
  () => props.entityId,
  async (newEntityId) => {
    if (newEntityId && newEntityId !== entity.value) {
      entity.value = newEntityId;
    }
    if (newEntityId) {
      await loadShareholderContacts(newEntityId);
    }
  },
  { immediate: true }
);

// 모든 Shareholder의 모든 Sub-class numberOfShares 값 변경 감지하여 validation 재실행
// ref를 사용하여 각 필드에 접근하고 validation을 강제로 다시 실행
const mainSharesFieldRefs = ref<Map<string, any>>(new Map());
const subClassSharesFieldRefs = ref<Map<string, any>>(new Map());

// 필드 ref 키 생성 함수
const getMainSharesFieldKey = (shareholderIndex: number, currencyIndex: number, classIndex: number) => {
  return `main-${shareholderIndex}-${currencyIndex}-${classIndex}`;
};

const getSubClassSharesFieldKey = (shareholderIndex: number, currencyIndex: number, classIndex: number, subClassIndex: number) => {
  return `subclass-${shareholderIndex}-${currencyIndex}-${classIndex}-${subClassIndex}`;
};

// 모든 Shareholder의 모든 Sub-class numberOfShares 값 변경 감지하여 validation 재실행
watch(
  () => shareholderSections.value.map(section => 
    section.currencySections.map(cs => 
      cs.shareClasses.map(sc => ({
        main: sc.numberOfShares,
        subClasses: sc.subClasses.map(sub => sub.numberOfShares).join(',')
      }))
    )
  ),
  async () => {
    // 값이 변경되면 다음 틱에서 모든 관련 필드의 validation을 강제로 다시 실행
    await nextTick();
    
    // 모든 관련 필드의 validation을 강제로 다시 실행
    mainSharesFieldRefs.value.forEach((fieldRef) => {
      if (fieldRef && typeof fieldRef.validate === 'function') {
        fieldRef.validate();
      }
    });
    
    subClassSharesFieldRefs.value.forEach((fieldRef) => {
      if (fieldRef && typeof fieldRef.validate === 'function') {
        fieldRef.validate();
      }
    });
  },
  { deep: true }
);

// Load class of shares
const loadClassOfShares = async () => {
  try {
    const response = await baseCodeApi.getCodesByMainCode('CM', 'A20', undefined);
    const codes = (response as any).codes || response.data?.codes || [];
    const unique = new Map<string, { title: string; value: string }>();
    codes.forEach((code: any) => {
      const total = String(code.totalCode || code.TOTAL_CODE || '').trim();
      const name = String(code.codeName || code.CODE_NAME || code.defaultCodeName || code.DEFAULT_CODE_NAME || '').trim();
      if (total && name && !unique.has(total) && 
          total !== '' && name !== '' && 
          !name.toLowerCase().includes('please select') &&
          !name.toLowerCase().includes('select') &&
          total.slice(-3) !== '000') {
        unique.set(total, { title: name, value: total });
      }
    });
    classOfShareOptions.value = Array.from(unique.values()).filter(item => item.value && item.value !== '');
  } catch (error) {
    console.error('Failed to load class of shares', error);
    classOfShareOptions.value = [];
  }
};

// Currency Options 로드
const loadCurrencyOptions = async () => {
  try {
    const response = await baseCodeApi.getCodesByMainCode('CM', 'B08', undefined);
    const codes = (response as any).codes || response.data?.codes || [];
    const unique = new Map<string, { title: string; value: string; modalTitle?: string; bigo3?: string; bigo1?: string }>();
    codes.forEach((code: any) => {
      const total = String(code.totalCode || code.TOTAL_CODE || '').trim();
      if (!total || unique.has(total) || total.slice(-3) === '000') {
        return;
      }
      const codeName = String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim();
      const bigo3 = String(code.bigo3 || code.BIGO3 || '').trim();
      const bigo1 = String(code.bigo1 || code.BIGO1 || '').trim();
      
      const listDisplay = bigo3 || codeName || total;
      const modalDisplay = bigo3 && codeName ? `${bigo3} (${codeName})` : listDisplay;
      
      unique.set(total, {
        title: listDisplay,
        value: total,
        modalTitle: modalDisplay,
        bigo3: bigo3,
        bigo1: bigo1
      });
    });
    
    const allOptions = Array.from(unique.values());
    const customerInfo = sessionStore.getCustomerInfo();
    const jurisdiction = customerInfo?.jurisdiction || '';
    
    const jurisdictionCurrencyMap: Record<string, string> = {
      'SG': 'SGD', 'MY': 'MYR', 'VN': 'VND', 'CN': 'CNY', 'HK': 'HKD',
      'KR': 'KRW', 'JP': 'JPY', 'TH': 'THB', 'ID': 'IDR', 'PH': 'PHP',
      'IN': 'INR', 'AU': 'AUD', 'GB': 'GBP', 'US': 'USD', 'TW': 'TWD',
    };
    
    const targetCurrencyCode = jurisdiction ? jurisdictionCurrencyMap[jurisdiction.toUpperCase()] : null;
    
    const sortedOptions = allOptions.sort((a, b) => {
      const aBigo3 = (a.bigo3 || '').toUpperCase();
      const bBigo3 = (b.bigo3 || '').toUpperCase();
      
      if (targetCurrencyCode) {
        if (aBigo3 === targetCurrencyCode && bBigo3 !== targetCurrencyCode) return -1;
        if (aBigo3 !== targetCurrencyCode && bBigo3 === targetCurrencyCode) return 1;
      }
      
      if (aBigo3 === 'USD' && bBigo3 !== 'USD') return -1;
      if (aBigo3 !== 'USD' && bBigo3 === 'USD') return 1;
      
      return aBigo3.localeCompare(bBigo3);
    });
    
    currencyOptions.value = sortedOptions;
  } catch (error) {
    console.error('❌ Failed to load currency options:', error);
    currencyOptions.value = [];
  }
};

// Load shareholder contacts
// Certificate Type 옵션 로드 (EntitySharesSection.vue와 동일하게 getEntityShares API 사용)
const loadCertificateTypes = async (entityId?: string) => {
  const targetEntityId = entityId || props.entityId || entity.value;
  if (!targetEntityId) {
    certificateTypeOptions.value = [];
    return;
  }
  
  try {
    // EntitySharesSection.vue와 동일하게 getEntityShares API 사용
    const response = await entityApi.getEntityShares(targetEntityId);
    const data = (response as any).data?.data || (response as any).data || {};
    const certificateTypeData = data.certificateTypeOptions || [];
    
    certificateTypeOptions.value = Array.isArray(certificateTypeData)
      ? certificateTypeData.map((item: any) => ({
          title: item.title || item.codeName || item.defaultCodeName || item.totalCode || item.TOTAL_CODE || '',
          value: item.value || item.totalCode || item.TOTAL_CODE || ''
        })).filter((item: any) => item.title)
      : [];
  } catch (error) {
    console.error('Failed to load certificate types', error);
    certificateTypeOptions.value = [];
  }
};

// Certificate No 자동 추천 (해당 Entity의 CERTI_NO 최대값 + 1)
const loadNextCertificateNo = async (entityId?: string) => {
  const targetEntityId = entityId || props.entityId || entity.value;
  if (!targetEntityId) return;
  try {
    const response: any = await getAllotmentNextCertificateNo(targetEntityId);
    const nextNo =
      response?.data?.data?.nextCertificateNo ||
      response?.data?.nextCertificateNo ||
      response?.nextCertificateNo ||
      '';
    if (String(nextNo).trim()) {
      certificateNo.value = String(nextNo).trim();
    }
  } catch (error) {
    console.warn('Failed to load next certificate no', error);
  }
};

// Chair Person 옵션 로드 (Entity의 Shareholder Contacts)
const loadChairPersonOptions = async (entityId?: string) => {
  const targetEntityId = entityId || props.entityId || entity.value;
  if (!targetEntityId) {
    chairPersonOptions.value = [];
    return;
  }
  try {
    const response = await entityApi.getEntityShareContacts(targetEntityId);
    const contacts = response.data?.data?.contacts || response.data?.contacts || [];
    const options = contacts.map((contact: any) => {
      const code = contact.CONTACT_CD || contact.contactCd || '';
      const name = contact.CONTACT_NAME || contact.contactName || '';
      return {
        title: name || code,
        value: code
      };
    });
    chairPersonOptions.value = options;
  } catch (error) {
    console.error('Failed to load chair person options', error);
    chairPersonOptions.value = [];
  }
};

// Representative Director 옵션 로드 (Entity의 Director 목록)
const loadRepresentativeDirectorOptions = async (entityId?: string) => {
  const targetEntityId = entityId || props.entityId || entity.value;
  if (!targetEntityId) {
    representativeDirectorOptions.value = [];
    return;
  }
  try {
    // Director positionCd는 'B30027' (일반 Director)
    const response = await getOfficersByEntity(targetEntityId, 'B30027');
    const officers = (response as any).data?.officers || (response as any).officers || [];
    const options = officers.map((officer: any) => {
      const code = officer.contactCd || officer.CONTACT_CD || '';
      const name = officer.contactName || officer.CONTACT_NAME || '';
      return {
        title: name || code,
        value: code
      };
    }).filter((opt: any) => opt.title && opt.value);
    representativeDirectorOptions.value = options;
  } catch (error) {
    console.error('Failed to load representative director options', error);
    representativeDirectorOptions.value = [];
  }
};

// RORC 옵션 로드 (Entity의 Controller 목록)
const loadRorcOptions = async (entityId?: string) => {
  const targetEntityId = entityId || props.entityId || entity.value;
  if (!targetEntityId) {
    rorcOptions.value = [];
    return;
  }
  try {
    const response = await getControllersByEntity(targetEntityId);
    const responseData = (response as any).data || response;
    const controllers = responseData?.controllers || responseData?.data?.controllers || [];
    const options = Array.isArray(controllers) ? controllers.map((controller: any) => {
      const id = controller.REGISTRABLE_CONTROLLER_ID || controller.registrableControllerId || '';
      const name = controller.FULL_NAME || controller.fullName || '';
      return {
        title: name || id,
        value: id
      };
    }).filter((opt: any) => opt.title && opt.value) : [];
    rorcOptions.value = options;
  } catch (error) {
    console.error('Failed to load RORC options', error);
    rorcOptions.value = [];
  }
};

// REPRESENTATIVE 옵션 로드 (RORC 선택 시 해당 Controller가 법인인 경우 해당 법인의 Director와 Shareholder)
const loadRepresentativeOptions = async (rorcId?: string) => {
  if (!rorcId || !entity.value) {
    representativeOptions.value = [];
    return;
  }
  
  try {
    // Controller 상세 정보 조회
    const controllerResponse = await getControllerDetail(entity.value, rorcId);
    const controllerData = (controllerResponse as any).data?.controller || 
                          (controllerResponse as any).data?.data?.controller || 
                          (controllerResponse as any).data || {};
    
    const categoryCode = controllerData.categoryCode || controllerData.CATEGORY_CD || '';
    const contactCd = controllerData.contactCd || controllerData.CONTACT_CD || '';
    
    // 법인인 경우만 (B09001 = Corporate)
    if (categoryCode === 'B09001' && contactCd) {
      // Director와 Shareholder 목록 병렬 조회
      const [directorResponse, shareholderResponse] = await Promise.all([
        getOfficersByEntity(contactCd, 'B30027'),
        entityApi.getEntityShareContacts(contactCd)
      ]);
      
      const directors = (directorResponse as any).data?.officers || 
                       (directorResponse as any).officers || [];
      const shareholders = (shareholderResponse as any).data?.data?.contacts || 
                          (shareholderResponse as any).data?.contacts || 
                          (shareholderResponse as any).contacts || [];
      
      // Director 옵션 생성
      const directorOptions = directors.map((director: any) => ({
        title: director.contactName || director.CONTACT_NAME || '',
        value: director.contactCd || director.CONTACT_CD || ''
      })).filter((opt: any) => opt.title && opt.value);
      
      // Shareholder 옵션 생성
      const shareholderOptions = shareholders.map((shareholder: any) => ({
        title: shareholder.CONTACT_NAME || shareholder.contactName || '',
        value: shareholder.CONTACT_CD || shareholder.contactCd || ''
      })).filter((opt: any) => opt.title && opt.value);
      
      // 두 목록 합치기 (중복 제거)
      const allOptions = [...directorOptions, ...shareholderOptions];
      const uniqueOptions = Array.from(
        new Map(allOptions.map(opt => [opt.value, opt])).values()
      );
      
      representativeOptions.value = uniqueOptions;
    } else {
      // 법인이 아니면 빈 목록
      representativeOptions.value = [];
    }
  } catch (error) {
    console.error('Failed to load REPRESENTATIVE options', error);
    representativeOptions.value = [];
  }
};

const loadShareholderContacts = async (entityId?: string) => {
  const targetEntityId = entityId || props.entityId || entity.value;
  if (!targetEntityId) {
    return;
  }
  isLoadingShareholders.value = true;
  try {
    const response = await entityApi.getEntityShareContacts(targetEntityId);
    const contacts = response.data?.data?.contacts || response.data?.contacts || [];
    const options = contacts.map((contact: any) => {
      const code = contact.CONTACT_CD || contact.contactCd || '';
      const name = contact.CONTACT_NAME || contact.contactName || '';
      return {
        title: name || code,
        value: code
      };
    });
    shareholderOptions.value = [...options];
  } catch (error) {
    console.error('❌ Failed to load shareholder contacts:', error);
    shareholderOptions.value = [];
  } finally {
    isLoadingShareholders.value = false;
  }
};

// 초기 데이터 로드
onMounted(async () => {
  try {
    await Promise.all([
      officerAppointmentStore.fetchEntities(),
      loadClassOfShares(),
      loadCurrencyOptions(),
      loadEntityTypeOptions(),
      loadSSICOptions(),
      loadBaseCodeOptions(),
      loadJurisdictionCodeNames()
    ]);
    if (props.entityId && !entity.value) {
      entity.value = props.entityId;
    }
    if (props.entityId || entity.value) {
      await Promise.all([
        loadShareholderContacts(),
        loadEntityProfile(props.entityId || entity.value),
        loadChairPersonOptions(props.entityId || entity.value),
        loadCertificateTypes(props.entityId || entity.value),
        loadNextCertificateNo(props.entityId || entity.value),
        loadRepresentativeDirectorOptions(props.entityId || entity.value),
        loadRorcOptions(props.entityId || entity.value)
      ]);
    }
  } catch (error) {
    console.error('Failed to fetch initial data:', error);
  }
});

// Contact 목록 모달 상태
const showContactListModal = ref(false);
const contactListInitialSearch = ref('');
const currentShareholderIndex = ref(0);

// Contact 상세 화면 모달
const showContactDetailModal = ref(false);
const currentContactDetailShareholderIndex = ref(0);

const handleNewContact = (shareholderIndex: number) => {
  const shareholderSection = shareholderSections.value[shareholderIndex];
  
  // Shareholder가 이미 선택되어 있으면 해당 Contact의 상세 화면으로 바로 이동
  if (shareholderSection.shareholder) {
    currentContactDetailShareholderIndex.value = shareholderIndex;
    showContactDetailModal.value = true;
  } else {
    // Shareholder가 선택되지 않았으면 Contact 리스트로 이동
    const selectedShareholderOption = shareholderOptions.value.find(opt => opt.value === shareholderSection.shareholder);
    contactListInitialSearch.value = selectedShareholderOption?.title || '';
    currentShareholderIndex.value = shareholderIndex;
    showContactListModal.value = true;
  }
};

const handleContactSelect = async (contact: any) => {
  const contactName = contact.CONTACT_NAME || `${contact.FIRST_NAME || ''} ${contact.SURNAME || ''}`.trim() || '';
  const contactCd = contact.CONTACT_CD || '';
  const section = shareholderSections.value[currentShareholderIndex.value];
  
  section.shareholder = contactCd;
  const existingOption = shareholderOptions.value.find(opt => opt.value === contactCd);
  if (!existingOption && contactName) {
    shareholderOptions.value.push({
      title: contactName,
      value: contactCd
    });
  }
  
  // Contact 선택 시 Contact 정보 로드 (문서 자동 선택은 watch에서 처리)
  if (contactCd) {
    await loadShareholderContactData(section.id, contactCd);
    // watch에서 자동으로 문서 선택을 처리하므로 여기서는 호출하지 않음
  }
  
  showContactListModal.value = false;
};

// Contact 상세 화면에서 선택 시 처리 (Select 버튼 클릭 시)
const handleContactDetailSelect = async (contact: any) => {
  if (contact && (contact.CONTACT_CD || contact.contactCd)) {
    const contactCd = contact.CONTACT_CD || contact.contactCd;
    const contactName = contact.CONTACT_NAME || contact.contactName || `${contact.FIRST_NAME || ''} ${contact.SURNAME || ''}`.trim() || '';
    const section = shareholderSections.value[currentContactDetailShareholderIndex.value];
    
    section.shareholder = contactCd;
    
    const existingOption = shareholderOptions.value.find(opt => opt.value === contactCd);
    if (!existingOption && contactName) {
      shareholderOptions.value.push({
        title: contactName,
        value: contactCd
      });
    }
    
    // Contact 선택 시 Contact 정보 로드 (문서 자동 선택은 watch에서 처리)
    if (contactCd) {
      await loadShareholderContactData(section.id, contactCd);
      // watch에서 자동으로 문서 선택을 처리하므로 여기서는 호출하지 않음
      await autoSelectTemplatesByShareholders();
    }
    
    showContactDetailModal.value = false;
  }
};

// Contact 상세 화면에서 수정 완료 후 데이터 업데이트
const handleContactUpdated = async (contact: any) => {
  if (!contact) return;
  
  const section = shareholderSections.value[currentContactDetailShareholderIndex.value];
  if (!section) return;
  
  const contactCd = contact.CONTACT_CD || contact.contactCd;
  if (!contactCd) return;
  
  // Contact 정보를 다시 로드하여 최신 데이터로 업데이트
  await loadShareholderContactData(section.id, contactCd);
  
  // validation 에러 초기화 (Contact 정보가 업데이트되었으므로)
  if (shareholderValidationErrors.value.has(section.id)) {
    shareholderValidationErrors.value.delete(section.id);
    shareholderValidationErrors.value = new Map(shareholderValidationErrors.value);
  }
  
  console.log('✅ [SharesAllotmentForm] Contact 정보 업데이트 완료:', contact);
};

// Shareholder Section 관리
const addShareholderSection = () => {
  const newId = String(shareholderSections.value.length + 1);
  shareholderSections.value.push({
    id: newId,
    shareholder: '',
    currencySections: [
      {
        id: '1',
        currency: '',
        sharesPayable: 'all-in-cash',
        shareClasses: [
          {
            id: `class-${Date.now()}`,
            classOfShares: '',
            numberOfShares: '',
            issuedShareCapital: '',
            paidUpShareCapital: '',
            subClasses: [],
            treasuryShares: []
          }
        ]
      }
    ]
  });
};

const deleteShareholderSection = (index: number) => {
  if (shareholderSections.value.length > 1) {
    shareholderSections.value.splice(index, 1);
  }
};

// Currency Section 관리
const addCurrencySection = (shareholderIndex: number) => {
  const shareholderSection = shareholderSections.value[shareholderIndex];
  const newId = String(shareholderSection.currencySections.length + 1);
  shareholderSection.currencySections.push({
    id: newId,
    currency: '',
    sharesPayable: 'all-in-cash',
    shareClasses: [
      {
        id: `class-${Date.now()}`,
        classOfShares: '',
        numberOfShares: '',
        issuedShareCapital: '',
        paidUpShareCapital: '',
        subClasses: [],
        treasuryShares: []
      }
    ]
  });
};

const deleteCurrencySection = (shareholderIndex: number, currencyIndex: number) => {
  const shareholderSection = shareholderSections.value[shareholderIndex];
  if (shareholderSection.currencySections.length > 1) {
    shareholderSection.currencySections.splice(currencyIndex, 1);
  }
};

// Share Class 관리
const addShareClass = (shareholderIndex: number, currencyIndex: number) => {
  const currencySection = shareholderSections.value[shareholderIndex].currencySections[currencyIndex];
  
  // 최대 3개까지만 추가 가능
  if (currencySection.shareClasses.length >= 3) {
    return;
  }
  
  const newId = `class-${Date.now()}`;
  currencySection.shareClasses.push({
    id: newId,
    classOfShares: '',
    numberOfShares: '',
    issuedShareCapital: '',
    paidUpShareCapital: '',
    subClasses: [],
    treasuryShares: []
  });
};

const deleteShareClass = (shareholderIndex: number, currencyIndex: number, classIndex: number) => {
  shareholderSections.value[shareholderIndex].currencySections[currencyIndex].shareClasses.splice(classIndex, 1);
};

// Sub-class 관리
const addSubClass = (shareholderIndex: number, currencyIndex: number, classIndex: number) => {
  const shareClass = shareholderSections.value[shareholderIndex].currencySections[currencyIndex].shareClasses[classIndex];
  const newId = `subclass-${Date.now()}`;
  shareClass.subClasses.push({
    id: newId,
    name: '',
    numberOfShares: ''
  });
};

const deleteSubClass = (shareholderIndex: number, currencyIndex: number, classIndex: number, subClassIndex: number) => {
  shareholderSections.value[shareholderIndex].currencySections[currencyIndex].shareClasses[classIndex].subClasses.splice(subClassIndex, 1);
};

// Treasury Shares 관리
const addTreasuryShares = (shareholderIndex: number, currencyIndex: number, classIndex: number) => {
  const shareClass = shareholderSections.value[shareholderIndex].currencySections[currencyIndex].shareClasses[classIndex];
  
  // 서비스 준비중 메시지 표시
  if (shareClass) {
    shareClass.showTreasuryMessage = true;
    // 5초 후 메시지 숨기기
    setTimeout(() => {
      shareClass.showTreasuryMessage = false;
    }, 5000);
  }
  
  // 기존 Treasury Shares 추가 로직은 주석 처리 (서비스 준비중이므로)
  // const newId = `treasury-${Date.now()}`;
  // shareClass.treasuryShares.push({
  //   id: newId
  // });
};

// 필수 검증 규칙
const shareholderRules = [
  (v: string) => !!v || (t('startTask.Shareholder') || 'Shareholder') + ' ' + (t('startTask.is required') || 'is required')
];

const currencyRules = [
  (v: string) => !!v || (t('startTask.Currency') || 'Currency') + ' ' + (t('startTask.is required') || 'is required')
];

const classOfSharesRules = [
  (v: string) => !!v || (t('startTask.Class of shares') || 'Class of shares') + ' ' + (t('startTask.is required') || 'is required')
];

const numberOfSharesRules = [
  (v: string) => !!v || (t('startTask.Number of Shares') || 'Number of Shares') + ' ' + (t('startTask.is required') || 'is required'),
  (v: string) => {
    if (!v) return true;
    const num = Number(v);
    return (!isNaN(num) && num > 0) || (t('startTask.Number of Shares must be greater than 0') || 'Number of Shares must be greater than 0');
  }
];

// Sub-class Number of Shares 총합과 일반 주식 Number of Shares 비교 검증 함수
// computed를 사용하여 reactive하게 만들기
const getSubClassValidationRules = (
  shareholderIndex: number,
  currencyIndex: number,
  classIndex: number
) => {
  // validation을 reactive하게 만들기 위해 computed 사용
  // 모든 Sub-class의 numberOfShares 값을 포함하여 reactive하게 만듦
  return computed(() => {
    const shareClass = shareholderSections.value[shareholderIndex]
      ?.currencySections[currencyIndex]
      ?.shareClasses[classIndex];
    
    // 모든 Sub-class의 numberOfShares 값을 computed에 포함하여 reactive하게 만듦
    const subClassValues = shareClass?.subClasses.map(sub => sub.numberOfShares).join(',') || '';
    const mainValue = shareClass?.numberOfShares || '';
    
    return [
      (v: string) => {
        if (!shareClass) return true;
        
        // Sub-class가 없으면 검증하지 않음
        if (shareClass.subClasses.length === 0) {
          return true;
        }
        
        // 일반 주식의 Number of Shares
        const mainNumberOfShares = shareClass.numberOfShares || '';
        const mainNum = mainNumberOfShares ? Number(mainNumberOfShares) : 0;
        
        // Sub-class들의 Number of Shares 총합 (모든 Sub-class 값 반영)
        const subClassTotal = shareClass.subClasses.reduce((sum: number, subClass: SubClass) => {
          const subNum = subClass.numberOfShares ? Number(subClass.numberOfShares) : 0;
          return sum + (isNaN(subNum) ? 0 : subNum);
        }, 0);
        
        // 총합이 같지 않으면 에러
        if (Math.abs(mainNum - subClassTotal) > 0.0001) { // 부동소수점 오차 고려
          return t('startTask.The sum of Sub-class Number of Shares must equal the main Number of Shares') || 
                 'The sum of Sub-class Number of Shares must equal the main Number of Shares';
        }
        
        return true;
      }
    ];
  });
};

// 일반 주식 Number of Shares 검증 함수 (Sub-class 총합과 비교)
// computed를 사용하여 reactive하게 만들기
const getMainNumberOfSharesValidationRules = (
  shareholderIndex: number,
  currencyIndex: number,
  classIndex: number
) => {
  // validation을 reactive하게 만들기 위해 computed 사용
  // 모든 Sub-class의 numberOfShares 값을 포함하여 reactive하게 만듦
  return computed(() => {
    const shareClass = shareholderSections.value[shareholderIndex]
      ?.currencySections[currencyIndex]
      ?.shareClasses[classIndex];
    
    // 모든 Sub-class의 numberOfShares 값을 computed에 포함하여 reactive하게 만듦
    const subClassValues = shareClass?.subClasses.map(sub => sub.numberOfShares).join(',') || '';
    
    return [
      (v: string) => {
        if (!shareClass) return true;
        
        // Sub-class가 없으면 검증하지 않음
        if (shareClass.subClasses.length === 0) {
          return true;
        }
        
        // 일반 주식의 Number of Shares (현재 입력값)
        const mainNumberOfShares = v || '';
        const mainNum = mainNumberOfShares ? Number(mainNumberOfShares) : 0;
        
        // Sub-class들의 Number of Shares 총합 (모든 Sub-class 값 반영)
        const subClassTotal = shareClass.subClasses.reduce((sum: number, subClass: SubClass) => {
          const subNum = subClass.numberOfShares ? Number(subClass.numberOfShares) : 0;
          return sum + (isNaN(subNum) ? 0 : subNum);
        }, 0);
        
        // 총합이 같지 않으면 에러
        if (Math.abs(mainNum - subClassTotal) > 0.0001) { // 부동소수점 오차 고려
          return t('startTask.The sum of Sub-class Number of Shares must equal the main Number of Shares') || 
                 'The sum of Sub-class Number of Shares must equal the main Number of Shares';
        }
        
        return true;
      }
    ];
  });
};

// EGMPlace 값 생성 (Registered Address 필드들을 이어서 붙임)
const getEGMPlace = (): string => {
  if (!entityProfileData.value) return '';
  
  const registeredAddress = entityProfileData.value?.registeredAddress || 
                           entityProfileData.value?.data?.registeredAddress || {};
  
  // formatAddress 함수를 사용하여 주소 포맷팅
  const formattedAddress = formatAddress(registeredAddress, { useComma: true });
  return typeof formattedAddress === 'string' ? formattedAddress : formattedAddress.join(', ');
};

// Shareholder별 validation 에러 상태 관리
const shareholderValidationErrors = ref<Map<string, Record<string, string>>>(new Map());

// 기본 필드 validation 에러 상태 관리 (Shareholder, Transaction Date 등)
const basicFieldValidationErrors = ref<Record<string, string>>({});

// Currency 필드 validation 에러 상태 관리 (shareholderIndex-currencyIndex를 키로 사용)
const currencyValidationErrors = ref<Map<string, string>>(new Map());

// Currency 필드 validation 에러 메시지 반환
const getCurrencyValidationError = (shareholderIndex: number, currencyIndex: number): string[] => {
  const key = `${shareholderIndex}-${currencyIndex}`;
  const error = currencyValidationErrors.value.get(key);
  if (error) {
    return [error];
  }
  return [];
};

// Class of Shares 필드 validation 에러 상태 관리 (shareholderIndex-currencyIndex-classIndex를 키로 사용)
const classOfSharesValidationErrors = ref<Map<string, string>>(new Map());

// Class of Shares 필드 validation 에러 메시지 반환
const getClassOfSharesValidationError = (shareholderIndex: number, currencyIndex: number, classIndex: number): string[] => {
  const key = `${shareholderIndex}-${currencyIndex}-${classIndex}`;
  const error = classOfSharesValidationErrors.value.get(key);
  if (error) {
    return [error];
  }
  return [];
};

// Number of Shares 필드 validation 에러 상태 관리 (shareholderIndex-currencyIndex-classIndex를 키로 사용)
const numberOfSharesValidationErrors = ref<Map<string, string>>(new Map());

// Number of Shares 필드 validation 에러 메시지 반환
const getNumberOfSharesValidationError = (shareholderIndex: number, currencyIndex: number, classIndex: number): string[] => {
  const key = `${shareholderIndex}-${currencyIndex}-${classIndex}`;
  const error = numberOfSharesValidationErrors.value.get(key);
  if (error) {
    return [error];
  }
  return [];
};

// 주소 표시 함수 (공통 함수 사용)
const formatShareholderAddress = (sectionId: string): string => {
  const contactData = getContactData(sectionId);
  if (!contactData) return '';
  const address = formatAddress(contactData, { useComma: true });
  return typeof address === 'string' ? address : '';
};

// 주소 필드 validation 함수
const validateShareholderAddress = (sectionId: string): boolean => {
  const section = shareholderSections.value.find(s => s.id === sectionId);
  if (!section || !section.shareholder) {
    return true; // Shareholder가 선택되지 않았으면 검증하지 않음
  }
  
  const contactData = getContactData(sectionId);
  if (!contactData) {
    // Contact 데이터가 없으면 주소 필수 체크 (Shareholder가 선택되었으므로 Contact 정보가 있어야 함)
    const errors: Record<string, string> = {};
    errors.address = t('startTask.This field is required') || 'This field is required';
    shareholderValidationErrors.value.set(sectionId, errors);
    return false;
  }
  
  // 주소 필드 자체를 validation (formatShareholderAddress 결과를 체크)
  const formattedAddress = formatShareholderAddress(sectionId);
  
  // 주소가 표시되고 있다면 (빈 문자열이 아니면) validation 통과
  if (formattedAddress && formattedAddress.trim()) {
    // 주소 관련 에러 제거
    const currentErrors = shareholderValidationErrors.value.get(sectionId) || {};
    delete currentErrors.address;
    delete currentErrors.postalCode;
    delete currentErrors.blockNo;
    delete currentErrors.streetName;
    delete currentErrors.buildingName;
    delete currentErrors.foreignAddressLine1;
    
    if (Object.keys(currentErrors).length === 0) {
      shareholderValidationErrors.value.delete(sectionId);
    } else {
      shareholderValidationErrors.value.set(sectionId, currentErrors);
    }
    
    return true;
  }
  
  // 주소가 비어있으면 에러 표시
  const errors: Record<string, string> = {};
  errors.address = t('startTask.This field is required') || 'This field is required';
  
  // 기존 에러에 추가 (Contact 에러와 병합)
  const existingErrors = shareholderValidationErrors.value.get(sectionId) || {};
  Object.assign(existingErrors, errors);
  
  if (Object.keys(existingErrors).length > 0) {
    shareholderValidationErrors.value.set(sectionId, existingErrors);
    return false;
  }
  
  return true;
};

// Contact 필드 validation 함수 (countryCode, mobileNo 필수값 체크 제거)
const validateShareholderContact = (sectionId: string): boolean => {
  const section = shareholderSections.value.find(s => s.id === sectionId);
  if (!section || !section.shareholder) {
    return true; // Shareholder가 선택되지 않았으면 검증하지 않음
  }
  
  // Contact 관련 에러 제거 (주소 에러는 유지)
  const currentErrors = shareholderValidationErrors.value.get(sectionId) || {};
  delete currentErrors.countryCode;
  delete currentErrors.mobileNo;
  
  if (Object.keys(currentErrors).length === 0) {
    shareholderValidationErrors.value.delete(sectionId);
  } else {
    shareholderValidationErrors.value.set(sectionId, currentErrors);
  }
  
  return true;
};

// Shareholder별 validation 에러 메시지 반환
const getShareholderValidationErrors = (sectionId: string, fieldName: string): string[] => {
  const errors = shareholderValidationErrors.value.get(sectionId);
  if (errors && errors[fieldName]) {
    return [errors[fieldName]];
  }
  return [];
};

// Currency 필드 변경 시 validation 에러 초기화
watch(() => shareholderSections.value.map(s => s.currencySections.map(cs => cs.currency)), () => {
  currencyValidationErrors.value.clear();
}, { deep: true });

// Class of Shares 필드 변경 시 validation 에러 초기화
watch(() => shareholderSections.value.map(s => s.currencySections.map(cs => cs.shareClasses.map(sc => sc.classOfShares))), () => {
  classOfSharesValidationErrors.value.clear();
}, { deep: true });

// Number of Shares 필드 변경 시 validation 에러 초기화
watch(() => shareholderSections.value.map(s => s.currencySections.map(cs => cs.shareClasses.map(sc => sc.numberOfShares))), () => {
  numberOfSharesValidationErrors.value.clear();
}, { deep: true });

// Shareholder 필드 변경 시 validation 에러 초기화
watch(() => shareholderSections.value.map(s => s.shareholder), () => {
  if (basicFieldValidationErrors.value.shareholder) {
    delete basicFieldValidationErrors.value.shareholder;
  }
}, { deep: true });

// Transaction Date 필드 변경 시 validation 에러 초기화
watch(transactionDateValue, () => {
  if (basicFieldValidationErrors.value.transactionDate) {
    delete basicFieldValidationErrors.value.transactionDate;
  }
});

// priorApprovalFiled가 'yes'로 변경되면 첫 번째 Shareholder 패널 자동으로 열기
watch(priorApprovalFiled, (newValue) => {
  if (newValue === 'yes' && shareholderSections.value.length > 0) {
    const firstShareholderPanelId = shareholderSections.value[0].id;
    // 이미 열려있지 않은 경우에만 추가
    if (!allExpandedPanels.value.includes(firstShareholderPanelId)) {
      allExpandedPanels.value.push(firstShareholderPanelId);
    }
  }
});

// 전체 Shareholder validation 체크 함수
const validateAllShareholders = async (): Promise<boolean> => {
  // 모든 에러 초기화
  shareholderValidationErrors.value.clear();
  
  // Vue 반응성 업데이트를 위해 nextTick 대기
  await nextTick();
  
  let hasError = false;
  let firstErrorSectionId: string | null = null;
  let firstErrorField: string | null = null;
  
  // 모든 Shareholder에 대해 validation 체크
  for (const section of shareholderSections.value) {
    if (!section.shareholder) {
      continue; // Shareholder가 선택되지 않은 경우는 건너뜀
    }
    
    const addressValid = validateShareholderAddress(section.id);
    const contactValid = validateShareholderContact(section.id);
    
    if (!addressValid || !contactValid) {
      hasError = true;
      if (!firstErrorSectionId) {
        firstErrorSectionId = section.id;
        const errors = shareholderValidationErrors.value.get(section.id) || {};
        // 주소 에러가 있으면 'address' 필드로 포커스, 외국 주소면 'foreignAddressLine1', 그 외는 첫 번째 에러 필드
        if (errors.address) {
          firstErrorField = 'address';
        } else if (errors.foreignAddressLine1) {
          firstErrorField = 'foreignAddressLine1';
        } else {
          firstErrorField = Object.keys(errors)[0] || null;
        }
      }
    }
  }
  
  // Vue 반응성 업데이트
  shareholderValidationErrors.value = new Map(shareholderValidationErrors.value);
  await nextTick();
  await new Promise(resolve => setTimeout(resolve, 50));
  
  // 첫 번째 에러 필드로 포커스 이동
  if (hasError && firstErrorSectionId && firstErrorField) {
    const field = document.querySelector(`[data-section-id="${firstErrorSectionId}"][data-field="${firstErrorField}"]`) as HTMLElement;
    if (field) {
      const inputElement = field.closest('.v-text-field, .v-autocomplete, .v-textarea') as HTMLElement;
      if (inputElement) {
        inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = inputElement.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement;
        if (input && typeof input.focus === 'function') {
          setTimeout(() => {
            input.focus();
          }, 100);
        }
      }
    }
  }
  
  return !hasError;
};

// 전체 필드 validation 체크 함수 (Shareholder, Transaction Date, Currency, Class of Shares, Number of Shares 등)
const validateAll = async (): Promise<boolean> => {
  // 모든 에러 초기화
  shareholderValidationErrors.value.clear();
  basicFieldValidationErrors.value = {};
  currencyValidationErrors.value.clear();
  classOfSharesValidationErrors.value.clear();
  numberOfSharesValidationErrors.value.clear();
  
  // Vue 반응성 업데이트를 위해 nextTick 대기
  await nextTick();
  
  let hasError = false;
  let firstErrorField: string | null = null;
  let firstErrorElement: HTMLElement | null = null;
  
  // 1. Shareholder 검증
  if (!shareholderSections.value.length || !shareholderSections.value[0]?.shareholder) {
    hasError = true;
    basicFieldValidationErrors.value.shareholder = t('startTask.This field is required') || 'This field is required';
    if (!firstErrorField) {
      firstErrorField = 'shareholder';
      const field = document.querySelector('[data-field="shareholder"]') as HTMLElement;
      if (field) {
        firstErrorElement = field.closest('.v-autocomplete') as HTMLElement;
      }
    }
  } else {
    delete basicFieldValidationErrors.value.shareholder;
  }
  
  // 2. Transaction Date 검증
  if (!transactionDateValue.value || !transactionDateValue.value.trim()) {
    hasError = true;
    basicFieldValidationErrors.value.transactionDate = t('startTask.This field is required') || 'This field is required';
    if (!firstErrorField) {
      firstErrorField = 'transactionDate';
      const field = document.querySelector('[data-field="transactionDate"]') as HTMLElement;
      if (field) {
        firstErrorElement = field.closest('.v-text-field') as HTMLElement;
      }
    }
  } else {
    delete basicFieldValidationErrors.value.transactionDate;
  }
  
  // 3. Currency, Class of Shares, Number of Shares 검증 (각 Shareholder의 각 Currency Section마다)
  for (let shareholderIndex = 0; shareholderIndex < shareholderSections.value.length; shareholderIndex++) {
    const shareholderSection = shareholderSections.value[shareholderIndex];
    
    // Shareholder가 선택되지 않은 경우는 Currency 검증을 건너뛰지만, Shareholder 자체는 이미 위에서 검증됨
    if (!shareholderSection.shareholder) {
      continue;
    }
    
    for (let currencyIndex = 0; currencyIndex < shareholderSection.currencySections.length; currencyIndex++) {
      const currencySection = shareholderSection.currencySections[currencyIndex];
      
      // Currency 검증
      if (!currencySection.currency || !currencySection.currency.trim()) {
        hasError = true;
        const currencyKey = `${shareholderIndex}-${currencyIndex}`;
        currencyValidationErrors.value.set(currencyKey, t('startTask.This field is required') || 'This field is required');
        if (!firstErrorField) {
          firstErrorField = `currency-${shareholderIndex}-${currencyIndex}`;
          const field = document.querySelector(`[data-shareholder-index="${shareholderIndex}"][data-currency-index="${currencyIndex}"][data-field="currency"]`) as HTMLElement;
          if (field) {
            firstErrorElement = field.closest('.v-autocomplete') as HTMLElement;
          }
        }
      } else {
        const currencyKey = `${shareholderIndex}-${currencyIndex}`;
        currencyValidationErrors.value.delete(currencyKey);
      }
      
      // Share Classes 검증
      for (let classIndex = 0; classIndex < currencySection.shareClasses.length; classIndex++) {
        const shareClass = currencySection.shareClasses[classIndex];
        
        // Class of Shares 검증
        if (!shareClass.classOfShares || !shareClass.classOfShares.trim()) {
          hasError = true;
          const classKey = `${shareholderIndex}-${currencyIndex}-${classIndex}`;
          classOfSharesValidationErrors.value.set(classKey, t('startTask.This field is required') || 'This field is required');
          if (!firstErrorField) {
            firstErrorField = `classOfShares-${shareholderIndex}-${currencyIndex}-${classIndex}`;
            const field = document.querySelector(`[data-shareholder-index="${shareholderIndex}"][data-currency-index="${currencyIndex}"][data-class-index="${classIndex}"][data-field="classOfShares"]`) as HTMLElement;
            if (field) {
              firstErrorElement = field.closest('.v-autocomplete') as HTMLElement;
            }
          }
        } else {
          const classKey = `${shareholderIndex}-${currencyIndex}-${classIndex}`;
          classOfSharesValidationErrors.value.delete(classKey);
        }
        
        // Number of Shares 검증
        if (!shareClass.numberOfShares || !shareClass.numberOfShares.trim()) {
          hasError = true;
          const sharesKey = `${shareholderIndex}-${currencyIndex}-${classIndex}`;
          numberOfSharesValidationErrors.value.set(sharesKey, t('startTask.This field is required') || 'This field is required');
          if (!firstErrorField) {
            firstErrorField = `numberOfShares-${shareholderIndex}-${currencyIndex}-${classIndex}`;
            const field = document.querySelector(`[data-shareholder-index="${shareholderIndex}"][data-currency-index="${currencyIndex}"][data-class-index="${classIndex}"][data-field="numberOfShares"]`) as HTMLElement;
            if (field) {
              firstErrorElement = field.closest('.v-text-field') as HTMLElement;
            }
          }
        } else {
          const sharesKey = `${shareholderIndex}-${currencyIndex}-${classIndex}`;
          numberOfSharesValidationErrors.value.delete(sharesKey);
        }
      }
    }
  }
  
  // 4. 주소 및 Contact 필드 검증
  const shareholdersValid = await validateAllShareholders();
  if (!shareholdersValid) {
    hasError = true;
  }
  
  // Vue 반응성 업데이트
  await nextTick();
  await new Promise(resolve => setTimeout(resolve, 50));
  
  // 첫 번째 에러 필드로 포커스 이동
  if (hasError) {
    if (firstErrorElement) {
      firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = firstErrorElement.querySelector('input') as HTMLInputElement;
      if (input && typeof input.focus === 'function') {
        setTimeout(() => {
          input.focus();
        }, 100);
      }
    } else if (firstErrorField) {
      // 필드명으로 찾기 시도
      const field = document.querySelector(`[data-field="${firstErrorField}"]`) as HTMLElement;
      if (field) {
        const inputElement = field.closest('.v-text-field, .v-autocomplete') as HTMLElement;
        if (inputElement) {
          inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = inputElement.querySelector('input') as HTMLInputElement;
          if (input && typeof input.focus === 'function') {
            setTimeout(() => {
              input.focus();
            }, 100);
          }
        }
      }
    }
  }
  
  return !hasError;
};

// TransferToShareholderID 값 생성 (개인: Identification No, 법인: Register No)
const getTransferToShareholderID = (): string => {
  const firstShareholderSection = shareholderSections.value[0];
  if (!firstShareholderSection?.shareholder) return '';
  
  const contactData = getContactData(firstShareholderSection.id);
  if (!contactData) return '';
  
  // Corporate인 경우 Register No, Individual인 경우 Identification No
  // 둘 다 CONTACT_REG_NO에 저장되어 있음
  return contactData.CONTACT_REG_NO || '';
};

// TransferToShareholderAddress 값 생성 (Residential Address 필드들을 이어서 붙임)
const getTransferToShareholderAddress = (): string => {
  const firstShareholderSection = shareholderSections.value[0];
  if (!firstShareholderSection?.shareholder) return '';
  
  const contactData = getContactData(firstShareholderSection.id);
  if (!contactData) return '';
  
  // formatAddress 함수를 사용하여 주소 포맷팅
  const formattedAddress = formatAddress(contactData, { useComma: true });
  return typeof formattedAddress === 'string' ? formattedAddress : formattedAddress.join(', ');
};

// TransferToShareholderBirth 값 생성 (Date of Birth)
const getTransferToShareholderBirth = (): string => {
  const firstShareholderSection = shareholderSections.value[0];
  if (!firstShareholderSection?.shareholder) return '';
  
  const contactData = getContactData(firstShareholderSection.id);
  if (!contactData) return '';
  
  const dateOfBirth = contactData.DATE_OF_BIRTH || '';
  // dd/mm/yyyy 형식으로 포맷팅
  return formatDateToDDMMYYYY(dateOfBirth);
};

// TransferToShareholderNationality 값 생성 (Nationality/Citizenship)
const getTransferToShareholderNationality = (): string => {
  const firstShareholderSection = shareholderSections.value[0];
  if (!firstShareholderSection?.shareholder) return '';
  
  const contactData = getContactData(firstShareholderSection.id);
  if (!contactData) return '';
  
  // NATIONALITY_CD 코드를 이름으로 변환
  return getNationalityDisplay(contactData.NATIONALITY_CD);
};

// Presen 값 생성 (Director 이름들을 모두 나열)
const getPresen = async (): Promise<string> => {
  if (!entity.value) return '';
  
  try {
    // Director positionCd는 'B30027' (일반 Director)
    const response = await getOfficersByEntity(entity.value, 'B30027');
    const officers = (response as any).data?.officers || (response as any).officers || [];
    
    // Director 이름들만 추출
    const directorNames = officers
      .map((officer: any) => officer.contactName || officer.CONTACT_NAME || '')
      .filter((name: string) => name && name.trim() !== '');
    
    // 이름들을 쉼표와 공백으로 연결
    return directorNames.join(', ');
  } catch (error) {
    console.error('❌ Failed to fetch Directors:', error);
    return '';
  }
};

// 문서 변수 매핑
const docVariableMapping = {
  entityName: 'CompanyName',
  entityRegNo: 'RegistrationNo',
  shareholderName: 'TransferToShareholder',
  transactionDate: 'ResolutionDate',
  noticeDate: 'NoticeDate',
  currencyName: 'Currency',
  classOfSharesName: 'ShareType',
  numberOfShares: 'NumberOfShares',
  issuedShareCapital: 'IssuedShareCapital',
  paidUpShareCapital: 'PaidUpShareCapital',
  sharesPayable: 'SharesPayable',
  egmPlace: 'EGMPlace',
  egmTime: 'EGMTime',
  egmDate: 'EGMDate',
  presen: 'Present',
  transferToShareholderID: 'TransferToShareholderID',
  transferToShareholderAddress: 'TransferToShareholderAddress',
  transferToShareholderBirth: 'TransferToShareholderBirth',
  transferToShareholderCertNo: 'TransferToShareholderCertNo',
  transferToShareholderNationality: 'TransferToShareholderNationality',
  chairPersonName: 'ChairpersonName',
  // Allotment 다중건 리스트용 변수
  allotmentShareholderList: 'AllotmentShareholderList', // TMP035/TMP032용
  allotmentShareholderListTMP036: 'AllotmentShareholderList', // TMP036용
};

// Helper 함수들
const getShareholderNameHelper = (shareholderCd: string): string => {
  const option = shareholderOptions.value.find(opt => opt.value === shareholderCd);
  return option?.title || '';
};

const getCurrencyNameHelper = (currencyCd: string): string => {
  const option = currencyOptionItems.value.find(opt => opt.value === currencyCd);
  return option?.title || '';
};

const getClassOfSharesNameHelper = (classCd: string): string => {
  const option = classOfShareOptions.value.find(opt => opt.value === classCd);
  return option?.title || '';
};

// 특정 Share Class에서 사용 가능한 Class of Shares 옵션 반환 (이미 선택된 것 제외)
// Vue의 반응성 시스템이 자동으로 의존성을 추적하므로 함수로도 반응형으로 작동
const getAvailableClassOfSharesOptions = (
  shareholderIndex: number,
  currencyIndex: number,
  currentClassIndex: number
) => {
  const currencySection = shareholderSections.value[shareholderIndex]?.currencySections[currencyIndex];
  if (!currencySection) return classOfShareOptions.value;
  
  // 현재 Share Class를 제외한 다른 Share Class들에서 이미 선택된 값들 수집
  const selectedValues = currencySection.shareClasses
    .map((sc, idx) => idx !== currentClassIndex ? sc.classOfShares : '')
    .filter(val => val && val.trim() !== '');
  
  // 이미 선택된 값들을 제외한 옵션만 반환
  return classOfShareOptions.value.filter(opt => !selectedValues.includes(opt.value));
};

const getChairPersonName = (): string => {
  if (!chairPerson.value) return '';
  const option = chairPersonOptions.value.find(opt => opt.value === chairPerson.value);
  return option?.title || '';
};

// formData 수집 (복수 구조 전체 지원 + 기존 인터페이스 호환)
const getFormData = () => {
  const selectedEntityOption = officerAppointmentStore.entityOptions.find(opt => opt.value === entity.value);
  const entityNameVal = selectedEntityOption?.title || '';
  const selectedEntity = officerAppointmentStore.entities.find((ent: any) =>
    (ent.entityCd || ent.ENTITY_CD) === entity.value
  );
  const entityRegNoVal = selectedEntity?.regNo || selectedEntity?.REG_NO || '';

  // 복수 구조 전체 데이터 수집
  const shareholders = shareholderSections.value.map((section) => ({
    shareholder: section.shareholder || '',
    shareholderName: getShareholderNameHelper(section.shareholder),
    currencies: section.currencySections.map((currencySection) => ({
      currency: currencySection.currency || '',
      currencyName: getCurrencyNameHelper(currencySection.currency),
      sharesPayable: currencySection.sharesPayable || '',
      shareClasses: currencySection.shareClasses.map((shareClass) => ({
        classOfShares: shareClass.classOfShares || '',
        classOfSharesName: getClassOfSharesNameHelper(shareClass.classOfShares),
        numberOfShares: shareClass.numberOfShares || '',
        issuedShareCapital: shareClass.issuedShareCapital || '',
        paidUpShareCapital: shareClass.paidUpShareCapital || '',
        subClasses: shareClass.subClasses.map((subClass) => ({
          name: subClass.name || '',
          numberOfShares: subClass.numberOfShares || ''
        })),
        treasuryShares: shareClass.treasuryShares || []
      }))
    }))
  }));

  // 첫 번째 데이터 (기존 인터페이스 호환)
  const firstShareholderSection = shareholderSections.value[0];
  const firstCurrencySection = firstShareholderSection?.currencySections[0];
  const firstClass = firstCurrencySection?.shareClasses[0];
  const selectedShareholderOption = shareholderOptions.value.find(opt => opt.value === firstShareholderSection?.shareholder);
  const shareholderNameVal = selectedShareholderOption?.title || '';
  const selectedCurrencyOption = currencyOptionItems.value.find(opt => opt.value === firstCurrencySection?.currency);
  const currencyNameVal = selectedCurrencyOption?.title || '';
  const selectedClassOption = classOfShareOptions.value.find(opt => opt.value === firstClass?.classOfShares);
  const classOfSharesNameVal = selectedClassOption?.title || '';

  return {
    // Entity 정보
    entity: entity.value || '',
    entityName: entityNameVal,
    entityRegNo: entityRegNoVal,
    transactionDate: transactionDateValue.value || '',
    priorApprovalFiled: priorApprovalFiled.value || '',
    
    // EGM 정보
    egmPlace: getEGMPlace(),
    egmTime: '10:00',
    egmDate: transactionDateValue.value || '',
    
    // 복수 구조 전체 데이터
    shareholders: shareholders,
    
    // 기존 인터페이스 호환 (첫 번째 데이터)
    shareholder: firstShareholderSection?.shareholder || '',
    shareholderName: shareholderNameVal,
    currency: firstCurrencySection?.currency || '',
    currencyName: currencyNameVal,
    classOfShares: firstClass?.classOfShares || '',
    classOfSharesName: classOfSharesNameVal,
    numberOfShares: firstClass?.numberOfShares || '',
    issuedShareCapital: firstClass?.issuedShareCapital || '',
    paidUpShareCapital: firstClass?.paidUpShareCapital || '',
    sharesPayable: firstCurrencySection?.sharesPayable || '',
    
    // 원본 데이터 (필요시)
    shareholderSections: shareholderSections.value
  };
};

// doc_variables 포함 formData (복수 구조 지원)
const getFormDataWithDocVariables = async () => {
  const formData = getFormData() as Record<string, any>;
  const fieldMap = new Map<string, { value: any; docVariables: string[] }>();

  // 값이 없어도 공백(' ')으로 doc_variables 생성 (빈 문자열은 문서 바인딩에서 식별이 안 됨)
  const addField = (key: string, docKey: string) => {
    const value = formData[key] || ' ';
    fieldMap.set(key, { value: value, docVariables: [docKey] });
  };

  // 기본 필드
  addField('entityName', docVariableMapping.entityName);
  addField('entityRegNo', docVariableMapping.entityRegNo);
  addField('shareholderName', docVariableMapping.shareholderName);
  addField('transactionDate', docVariableMapping.transactionDate);
  addField('noticeDate', docVariableMapping.noticeDate);
  addField('currencyName', docVariableMapping.currencyName);
  addField('classOfSharesName', docVariableMapping.classOfSharesName);
  addField('numberOfShares', docVariableMapping.numberOfShares);
  addField('issuedShareCapital', docVariableMapping.issuedShareCapital);
  addField('paidUpShareCapital', docVariableMapping.paidUpShareCapital);
  addField('sharesPayable', docVariableMapping.sharesPayable);
  
  // EGM 필드
  addField('egmPlace', docVariableMapping.egmPlace);
  addField('egmTime', docVariableMapping.egmTime);
  addField('egmDate', docVariableMapping.egmDate);
  
  // Presen 필드 (비동기로 Director 목록 가져오기) - 값이 없어도 공백으로 생성
  const presenValue = await getPresen();
  fieldMap.set('presen', { 
    value: presenValue || ' ', 
    docVariables: [docVariableMapping.presen] 
  });
  
  // TransferToShareholder 필드 - 값이 없어도 공백으로 생성
  const transferToShareholderID = getTransferToShareholderID();
  fieldMap.set('transferToShareholderID', { 
    value: transferToShareholderID || ' ', 
    docVariables: [docVariableMapping.transferToShareholderID] 
  });
  
  const transferToShareholderAddress = getTransferToShareholderAddress();
  fieldMap.set('transferToShareholderAddress', { 
    value: transferToShareholderAddress || ' ', 
    docVariables: [docVariableMapping.transferToShareholderAddress] 
  });
  
  const transferToShareholderBirth = getTransferToShareholderBirth();
  fieldMap.set('transferToShareholderBirth', { 
    value: transferToShareholderBirth || ' ', 
    docVariables: [docVariableMapping.transferToShareholderBirth] 
  });
  
  const transferToShareholderNationality = getTransferToShareholderNationality();
  fieldMap.set('transferToShareholderNationality', { 
    value: transferToShareholderNationality || ' ', 
    docVariables: [docVariableMapping.transferToShareholderNationality] 
  });
  
  // Certificate No 필드 - 값이 없어도 공백으로 생성
  fieldMap.set('transferToShareholderCertNo', { 
    value: certificateNo.value || ' ', 
    docVariables: [docVariableMapping.transferToShareholderCertNo] 
  });
  
  // Chair Person 필드 - 값이 없어도 공백으로 생성
  const chairPersonNameVal = getChairPersonName();
  fieldMap.set('chairPersonName', { 
    value: chairPersonNameVal || ' ', 
    docVariables: [docVariableMapping.chairPersonName] 
  });

  // AllotmentShareholderList 생성 (TMP036용과 TMP035/TMP032용 분리)
  const allotmentLinesTMP036: string[] = [];
  const allotmentLinesTMP035: string[] = [];
  const shareholdersForList = (formData.shareholders || []) as any[];

  shareholdersForList.forEach((sh: any) => {
    const shareholderName =
      (sh.shareholderName && sh.shareholderName.value) || sh.shareholderName || '';
    const currencies = sh.currencies || [];

    currencies.forEach((cur: any) => {
      const currency =
        (cur.currencyName && cur.currencyName.value) || cur.currencyName || '';
      const shareClasses = cur.shareClasses || [];

      shareClasses.forEach((sc: any) => {
        const numberOfShares =
          (sc.numberOfShares && sc.numberOfShares.value) || sc.numberOfShares || '';
        const shareType =
          (sc.classOfSharesName && sc.classOfSharesName.value) ||
          sc.classOfSharesName ||
          '';
        const issuedShareCapital =
          (sc.issuedShareCapital && sc.issuedShareCapital.value) ||
          sc.issuedShareCapital ||
          '';

        // TMP036용 블록 (일반 공백 패딩, 탭 제거, Share Capital 좌측 정렬)
        const blockLinesTMP036: string[] = [];
        const label1 = 'Number of Shares';
        const label2 = 'Security';
        const label3Line1 = 'Amount of Issued';
        const label3Line2 = 'Share Capital';

        // 최대 길이 계산 (18자)
        const maxLengthTMP036 = Math.max(label1.length, label2.length, label3Line1.length);

        // 일반 공백으로 패딩 (원래대로)
        const paddedLabel1 = label1.padEnd(maxLengthTMP036);
        const paddedLabel2 = label2.padEnd(maxLengthTMP036);
        const paddedLabel3Line1 = label3Line1.padEnd(maxLengthTMP036);

        if (numberOfShares) {
          // 탭 제거: paddedLabel1\t: → paddedLabel1 :
          blockLinesTMP036.push(`${paddedLabel1} : ${numberOfShares}`);
        }
        if (shareType) {
          blockLinesTMP036.push(`${paddedLabel2}\t: ${shareType}`);
        }
        if (issuedShareCapital && currency) {
          // 탭 제거: paddedLabel3Line1\t: → paddedLabel3Line1 :
          blockLinesTMP036.push(
            `${paddedLabel3Line1} : (${currency})${issuedShareCapital}`
          );
          // Share Capital 좌측에 붙이기 (들여쓰기 제거)
          blockLinesTMP036.push(`${label3Line2}`);
        }

        if (blockLinesTMP036.length > 0) {
          allotmentLinesTMP036.push(blockLinesTMP036.join('\n'));
        }

        // TMP035/TMP032용 블록 (탭 문자로 콜론 정렬)
        const blockLinesTMP035: string[] = [];
        const label4 = 'Shareholder';
        const label5 = 'No of Shares';
        const label6 = 'Share Type';

        if (shareholderName) {
          blockLinesTMP035.push(`${label4}\t : ${shareholderName}`);
        }
        if (numberOfShares && currency) {
          blockLinesTMP035.push(`${label5}\t : (${currency})${numberOfShares}`);
        }
        if (shareType) {
          blockLinesTMP035.push(`${label6}\t : ${shareType}`);
        }

        if (blockLinesTMP035.length > 0) {
          allotmentLinesTMP035.push(blockLinesTMP035.join('\n'));
        }
      });
    });
  });

  const allotmentShareholderListTMP036Val =
    allotmentLinesTMP036.length > 0 ? allotmentLinesTMP036.join('\n\n') : '';
  const allotmentShareholderListTMP035Val =
    allotmentLinesTMP035.length > 0 ? allotmentLinesTMP035.join('\n\n') : '';

  // TMP036용 변수
  fieldMap.set('allotmentShareholderListTMP036', {
    value: allotmentShareholderListTMP036Val || ' ',
    docVariables: [docVariableMapping.allotmentShareholderListTMP036],
  });

  // TMP035/TMP032용 변수 (기존 변수명 유지)
  fieldMap.set('allotmentShareholderList', {
    value: allotmentShareholderListTMP035Val || ' ',
    docVariables: [docVariableMapping.allotmentShareholderList],
  });

  // Entity 관련 공통 데이터 조회 및 doc_variables 생성
  if (entity.value) {
    try {
      const entityData = await getEntityData(entity.value);
      
      // {#CompanyName} - entityName에 추가 (기존 필드와 병합)
      if (entityData.companyName) {
        const existingField = fieldMap.get('entityName');
        if (existingField) {
          // 기존 필드가 있으면 docVariables만 추가 (중복 제거)
          if (!existingField.docVariables.includes('CompanyName')) {
            existingField.docVariables.push('CompanyName');
          }
        } else {
          // 없으면 새로 추가
          fieldMap.set('entityName', {
            value: entityData.companyName,
            docVariables: ['CompanyName']
          });
        }
      }

      // {#RegistrationNo} - entityRegNo에 추가 (기존 필드와 병합)
      if (entityData.registrationNumber) {
        const existingField = fieldMap.get('entityRegNo');
        if (existingField) {
          // 기존 필드가 있으면 docVariables만 추가 (중복 제거)
          if (!existingField.docVariables.includes('RegistrationNo')) {
            existingField.docVariables.push('RegistrationNo');
          }
        } else {
          // 없으면 새로 추가
          fieldMap.set('entityRegNo', {
            value: entityData.registrationNumber,
            docVariables: ['RegistrationNo']
          });
        }
      }

      // {#IncorporatedDate} - DATE_OF_INCORPORATION 날짜 형식 (formatDocDate 사용)
      if (entityData.incorporationDate) {
        // DD/MM/YYYY 형식을 YYYY-MM-DD로 변환
        let dateToFormat = entityData.incorporationDate;
        const ddMMyyyyMatch = String(dateToFormat).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddMMyyyyMatch) {
          const [, day, month, year] = ddMMyyyyMatch;
          dateToFormat = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        const formattedDate = formatDocDate(dateToFormat);
        if (formattedDate) {
          fieldMap.set('incorporatedDate', {
            value: formattedDate,
            docVariables: ['IncorporatedDate']
          });
        }
      }

      // {#CompanyAddress}
      if (entityData.companyAddress) {
        fieldMap.set('companyAddress', {
          value: entityData.companyAddress,
          docVariables: ['CompanyAddress']
        });
      }

      // {#FinancialYearEnd} - FINANCIAL_YEAR_END 날짜 형식 (formatDocDate 사용)
      if (entityData.financialYearEnd) {
        // DD/MM/YYYY 형식을 YYYY-MM-DD로 변환
        let dateToFormat = entityData.financialYearEnd;
        const ddMMyyyyMatch = String(dateToFormat).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddMMyyyyMatch) {
          const [, day, month, year] = ddMMyyyyMatch;
          dateToFormat = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        const formattedDate = formatDocDate(dateToFormat);
        if (formattedDate) {
          fieldMap.set('financialYearEnd', {
            value: formattedDate,
            docVariables: ['FinancialYearEnd']
          });
        }
      }

      // {#PrimaryActivityCode}
      if (entityData.primaryActivityCode) {
        fieldMap.set('primaryActivityCode', {
          value: entityData.primaryActivityCode,
          docVariables: ['PrimaryActivityCode']
        });
      }

      // {#PrimaryActivityDescription}
      if (entityData.primaryActivityDescription) {
        fieldMap.set('primaryActivityDescription', {
          value: entityData.primaryActivityDescription,
          docVariables: ['PrimaryActivityDescription']
        });
      }

      // {#SecondaryActivityCode}
      if (entityData.secondaryActivityCode) {
        fieldMap.set('secondaryActivityCode', {
          value: entityData.secondaryActivityCode,
          docVariables: ['SecondaryActivityCode']
        });
      }

      // {#SecondaryActivityDescription}
      if (entityData.secondaryActivityDescription) {
        fieldMap.set('secondaryActivityDescription', {
          value: entityData.secondaryActivityDescription,
          docVariables: ['SecondaryActivityDescription']
        });
      }

      // {#EntityJurisdiction}
      if (entityData.entityJurisdiction) {
        fieldMap.set('entityJurisdiction', {
          value: entityData.entityJurisdiction,
          docVariables: ['EntityJurisdiction']
        });
      }

      // {#EntityType}
      if (entityData.entityType) {
        fieldMap.set('entityType', {
          value: entityData.entityType,
          docVariables: ['EntityType']
        });
      }

      // {#CompanyEmail}
      if (entityData.companyEmail) {
        fieldMap.set('companyEmail', {
          value: entityData.companyEmail,
          docVariables: ['CompanyEmail']
        });
      }
    } catch (error) {
      console.error('❌ [SharesAllotmentForm] Entity 데이터 조회 실패:', error);
    }
  }

  const result: Record<string, any> = {};
  fieldMap.forEach((item, fieldName) => {
    result[fieldName] = {
      value: item.value,
      doc_variables: item.docVariables.map(v => `#${v}`)
    };
  });

  // 복수 구조 Shareholder 데이터에 doc_variables 부여 (Shareholder별 문서 바인딩용)
  const shareholdersWithDocVars = (formData.shareholders || []).map((sh: any) => {
    const firstCurrency = (sh.currencies && sh.currencies[0]) || {};
    const firstClass = (firstCurrency.shareClasses && firstCurrency.shareClasses[0]) || {};

    const shareholderEntry: any = {
      // 원본 데이터 유지 (필요 시 사용)
      ...sh
    };

    // Shareholder 이름
    shareholderEntry.shareholderName = {
      value: sh.shareholderName || ' ',
      doc_variables: [`#${docVariableMapping.shareholderName}`]
    };

    // Currency 이름
    shareholderEntry.currencyName = {
      value: firstCurrency.currencyName || ' ',
      doc_variables: [`#${docVariableMapping.currencyName}`]
    };

    // Class of Shares 이름
    shareholderEntry.classOfSharesName = {
      value: firstClass.classOfSharesName || ' ',
      doc_variables: [`#${docVariableMapping.classOfSharesName}`]
    };

    // Shares 수량 / Capital / Shares Payable
    shareholderEntry.numberOfShares = {
      value: firstClass.numberOfShares || ' ',
      doc_variables: [`#${docVariableMapping.numberOfShares}`]
    };

    shareholderEntry.issuedShareCapital = {
      value: firstClass.issuedShareCapital || ' ',
      doc_variables: [`#${docVariableMapping.issuedShareCapital}`]
    };

    shareholderEntry.paidUpShareCapital = {
      value: firstClass.paidUpShareCapital || ' ',
      doc_variables: [`#${docVariableMapping.paidUpShareCapital}`]
    };

    shareholderEntry.sharesPayable = {
      value: firstCurrency.sharesPayable || ' ',
      doc_variables: [`#${docVariableMapping.sharesPayable}`]
    };

    // ✅ 각 currency에도 doc_variables 부여
    if (shareholderEntry.currencies && Array.isArray(shareholderEntry.currencies)) {
      shareholderEntry.currencies = shareholderEntry.currencies.map((cur: any) => {
        const currencyWithDocVars: any = { ...cur };
        
        // currencyName에 doc_variables 부여
        if (currencyWithDocVars.currencyName) {
          const currencyNameValue = typeof currencyWithDocVars.currencyName === 'string' 
            ? currencyWithDocVars.currencyName 
            : (currencyWithDocVars.currencyName.value || currencyWithDocVars.currencyName);
          
          currencyWithDocVars.currencyName = {
            value: currencyNameValue || ' ',
            doc_variables: [`#${docVariableMapping.currencyName}`]
          };
        }
        
        // shareClasses에도 doc_variables 부여
        if (currencyWithDocVars.shareClasses && Array.isArray(currencyWithDocVars.shareClasses)) {
          currencyWithDocVars.shareClasses = currencyWithDocVars.shareClasses.map((sc: any) => {
            const shareClassWithDocVars: any = { ...sc };
            
            if (shareClassWithDocVars.classOfSharesName) {
              const classOfSharesNameValue = typeof shareClassWithDocVars.classOfSharesName === 'string'
                ? shareClassWithDocVars.classOfSharesName
                : (shareClassWithDocVars.classOfSharesName.value || shareClassWithDocVars.classOfSharesName);
              
              shareClassWithDocVars.classOfSharesName = {
                value: classOfSharesNameValue || ' ',
                doc_variables: [`#${docVariableMapping.classOfSharesName}`]
              };
            }
            
            if (shareClassWithDocVars.numberOfShares) {
              const numberOfSharesValue = typeof shareClassWithDocVars.numberOfShares === 'string'
                ? shareClassWithDocVars.numberOfShares
                : (shareClassWithDocVars.numberOfShares.value || shareClassWithDocVars.numberOfShares);
              
              shareClassWithDocVars.numberOfShares = {
                value: numberOfSharesValue || ' ',
                doc_variables: [`#${docVariableMapping.numberOfShares}`]
              };
            }
            
            if (shareClassWithDocVars.issuedShareCapital) {
              const issuedShareCapitalValue = typeof shareClassWithDocVars.issuedShareCapital === 'string'
                ? shareClassWithDocVars.issuedShareCapital
                : (shareClassWithDocVars.issuedShareCapital.value || shareClassWithDocVars.issuedShareCapital);
              
              shareClassWithDocVars.issuedShareCapital = {
                value: issuedShareCapitalValue || ' ',
                doc_variables: [`#${docVariableMapping.issuedShareCapital}`]
              };
            }
            
            if (shareClassWithDocVars.paidUpShareCapital) {
              const paidUpShareCapitalValue = typeof shareClassWithDocVars.paidUpShareCapital === 'string'
                ? shareClassWithDocVars.paidUpShareCapital
                : (shareClassWithDocVars.paidUpShareCapital.value || shareClassWithDocVars.paidUpShareCapital);
              
              shareClassWithDocVars.paidUpShareCapital = {
                value: paidUpShareCapitalValue || ' ',
                doc_variables: [`#${docVariableMapping.paidUpShareCapital}`]
              };
            }
            
            return shareClassWithDocVars;
          });
        }
        
        return currencyWithDocVars;
      });
    }

    return shareholderEntry;
  });

  result.shareholders = shareholdersWithDocVars;
  
  return result;
};

// ========================================
// E-Filing JSON 생성 관련 코드
// ========================================

// 날짜를 "20 Jan 2026" 형식으로 변환
const formatDateForEFiling = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    // YYYY-MM-DD 형식 또는 YYYY-MM-DD HH:mm:ss 형식인 경우
    let datePart = dateString.trim();
    
    // "1982-03-12 00:00:00.0" 형식에서 날짜 부분만 추출
    if (datePart.includes(' ')) {
      datePart = datePart.split(' ')[0];
    }
    
    // YYYY-MM-DD 형식인지 확인
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const date = new Date(datePart + 'T00:00:00');
      if (isNaN(date.getTime())) return dateString;
      
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day} ${month} ${year}`;
    }
    
    // 이미 다른 형식인 경우 그대로 반환
    return dateString;
  } catch (e) {
    console.warn('날짜 포맷팅 실패:', e);
    return dateString;
  }
};

// Country 코드에서 BIGO3 값 가져오기 (+ 제거)
const getCountryBigo3WithoutPlus = (countryCd: string): string => {
  const countryItem = countryOptions.value.find(opt => opt.value === countryCd);
  const bigo3 = countryItem?.bigo3 || '';
  return bigo3.replace(/^\+/, '');
};

// Class of Shares 코드에서 축약형 가져오기 (A20001 -> ORD, A20002 -> PREF, A20003 -> OTH)
const getClassOfSharesAs = (classOfSharesCd: string): string => {
  if (!classOfSharesCd) return '';
  const classOfSharesItem = classOfShareOptions.value.find(opt => opt.value === classOfSharesCd);
  // 코드에서 직접 매핑
  if (classOfSharesCd === 'A20001') return 'ORD';
  if (classOfSharesCd === 'A20002') return 'PREF';
  if (classOfSharesCd === 'A20003') return 'OTH';
  // 옵션에서 찾기
  const title = classOfSharesItem?.title || '';
  if (title.toLowerCase().includes('ordinary')) return 'ORD';
  if (title.toLowerCase().includes('preference') || title.toLowerCase().includes('pref')) return 'PREF';
  if (title.toLowerCase().includes('other')) return 'OTH';
  return '';
};

// Currency 코드에서 축약형 가져오기 (BIGO3 사용)
const getCurrencyAs = (currencyCd: string): string => {
  if (!currencyCd) return '';
  const currencyItem = currencyOptionItems.value.find(opt => opt.value === currencyCd);
  return currencyItem?.bigo3 || '';
};

// Shares Payable 텍스트 변환 (all-in-cash -> In Cash 등)
const getSharesPayableText = (sharesPayable: string): string => {
  if (!sharesPayable) return '';
  const textMap: Record<string, string> = {
    'all-in-cash': 'In Cash',
    'all-otherwise': 'Otherwise than in Cash',
    'no-consideration': 'No Consideration',
    'partially': 'Partially in Cash and Otherwise than in Cash'
  };
  return textMap[sharesPayable] || sharesPayable;
};

// { code, name } 객체를 name 값으로 변환하는 함수
const transformCodeNameToValue = (data: any): any => {
  if (data === null || data === undefined) {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(item => transformCodeNameToValue(item));
  }

  if (typeof data === 'object') {
    const keys = Object.keys(data);
    if (keys.length === 2 && keys.includes('code') && keys.includes('name')) {
      return data.name || data.code || '';
    }

    const result: any = {};
    for (const key of keys) {
      result[key] = transformCodeNameToValue(data[key]);
    }
    return result;
  }

  return data;
};

// 외부에서 폼 데이터 설정 (E-Filing 페이지에서 TASK_DATA로 데이터 채우기용)
const setFormData = async (formData: any) => {
  if (!formData) {
    console.warn('⚠️ [SharesAllotmentForm] setFormData: formData가 null 또는 undefined입니다');
    return;
  }

  console.log('📋 [SharesAllotmentForm] setFormData:', formData);

  // 데이터 복원 모드 시작
  isRestoringData.value = true;

  try {
    // Entity 설정
    if (formData.entity) {
      entity.value = formData.entity;
      // Entity 설정 후 Vue 반응성 시스템이 처리될 때까지 대기
      await nextTick();
      // Entity 관련 데이터 로드 완료 대기
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    // Transaction Date 설정
    if (formData.transactionDate?.value) {
      transactionDateValue.value = formData.transactionDate.value;
    } else if (formData.transactionDate) {
      transactionDateValue.value = formData.transactionDate;
    }

    // Prior Approval Filed 설정
    if (formData.priorApprovalFiled) {
      priorApprovalFiled.value = formData.priorApprovalFiled;
    }

    // Certificate 관련 설정
    if (formData.certificateType) {
      certificateType.value = formData.certificateType;
    }
    if (formData.certificateNo) {
      certificateNo.value = formData.certificateNo;
    }

    // Chair Person 설정
    if (formData.chairPerson) {
      chairPerson.value = formData.chairPerson;
    }

    // Representative Director, RORC, Representative 설정
    if (formData.representativeDirector) {
      representativeDirector.value = formData.representativeDirector;
    }
    if (formData.rorc) {
      rorc.value = formData.rorc;
    }
    if (formData.representative) {
      representative.value = formData.representative;
    }

    // Shareholder Sections 복원 (복수 Shareholder 지원)
    if (formData.shareholders && Array.isArray(formData.shareholders) && formData.shareholders.length > 0) {
      const restoredSections: ShareholderSection[] = [];

      for (let i = 0; i < formData.shareholders.length; i++) {
        const shareholderData = formData.shareholders[i];
        const sectionId = String(i + 1);

        // Currency Sections 복원
        const currencySections: CurrencySection[] = [];
        if (shareholderData.currencies && Array.isArray(shareholderData.currencies)) {
          for (let j = 0; j < shareholderData.currencies.length; j++) {
            const currencyData = shareholderData.currencies[j];
            const currencySectionId = String(j + 1);

            // Share Classes 복원
            const shareClasses: ShareClass[] = [];
            if (currencyData.shareClasses && Array.isArray(currencyData.shareClasses)) {
              for (let k = 0; k < currencyData.shareClasses.length; k++) {
                const shareClassData = currencyData.shareClasses[k];
                const shareClassId = `class-${Date.now()}-${k}`;

                // 값 추출 헬퍼 함수 (객체인 경우 value 속성 사용)
                const getValue = (val: any): string => {
                  if (val === null || val === undefined) return '';
                  if (typeof val === 'string') return val;
                  if (typeof val === 'object' && 'value' in val) return String(val.value || '');
                  return String(val);
                };

                // SubClasses 복원
                const subClasses: SubClass[] = [];
                if (shareClassData.subClasses && Array.isArray(shareClassData.subClasses)) {
                  subClasses.push(...shareClassData.subClasses.map((sc: any, idx: number) => ({
                    id: `subclass-${idx}`,
                    name: sc.name || '',
                    numberOfShares: getValue(sc.numberOfShares)
                  })));
                }

                shareClasses.push({
                  id: shareClassId,
                  classOfShares: getValue(shareClassData.classOfShares),
                  numberOfShares: getValue(shareClassData.numberOfShares),
                  issuedShareCapital: getValue(shareClassData.issuedShareCapital),
                  paidUpShareCapital: getValue(shareClassData.paidUpShareCapital),
                  subClasses: subClasses,
                  treasuryShares: []
                });
              }
            }

            currencySections.push({
              id: currencySectionId,
              currency: currencyData.currency || '',
              sharesPayable: currencyData.sharesPayable || 'all-in-cash',
              shareClasses: shareClasses.length > 0 ? shareClasses : [{
                id: `class-${Date.now()}`,
                classOfShares: '',
                numberOfShares: '',
                issuedShareCapital: '',
                paidUpShareCapital: '',
                subClasses: [],
                treasuryShares: []
              }]
            });
          }
        }

        restoredSections.push({
          id: sectionId,
          shareholder: shareholderData.shareholder || '',
          currencySections: currencySections.length > 0 ? currencySections : [{
            id: '1',
            currency: '',
            sharesPayable: 'all-in-cash',
            shareClasses: [{
              id: `class-${Date.now()}`,
              classOfShares: '',
              numberOfShares: '',
              issuedShareCapital: '',
              paidUpShareCapital: '',
              subClasses: [],
              treasuryShares: []
            }]
          }]
        });

        // Shareholder 설정 후 Contact 정보 로드
        if (shareholderData.shareholder) {
          await loadShareholderContactData(sectionId, shareholderData.shareholder);
          await nextTick();
        }
      }

      shareholderSections.value = restoredSections;
    } else if (formData.shareholder) {
      // 단일 Shareholder (기존 인터페이스 호환)
      if (shareholderSections.value.length > 0) {
        shareholderSections.value[0].shareholder = formData.shareholder;
        
        // Currency, Class of Shares 등 복원
        if (formData.currency) {
          if (shareholderSections.value[0].currencySections.length > 0) {
            shareholderSections.value[0].currencySections[0].currency = formData.currency;
          }
        }
        if (formData.classOfShares) {
          if (shareholderSections.value[0].currencySections.length > 0) {
            if (shareholderSections.value[0].currencySections[0].shareClasses.length > 0) {
              shareholderSections.value[0].currencySections[0].shareClasses[0].classOfShares = formData.classOfShares;
            }
          }
        }
        if (formData.numberOfShares) {
          if (shareholderSections.value[0].currencySections.length > 0) {
            if (shareholderSections.value[0].currencySections[0].shareClasses.length > 0) {
              shareholderSections.value[0].currencySections[0].shareClasses[0].numberOfShares = formData.numberOfShares;
            }
          }
        }
        if (formData.issuedShareCapital) {
          if (shareholderSections.value[0].currencySections.length > 0) {
            if (shareholderSections.value[0].currencySections[0].shareClasses.length > 0) {
              shareholderSections.value[0].currencySections[0].shareClasses[0].issuedShareCapital = formData.issuedShareCapital;
            }
          }
        }
        if (formData.paidUpShareCapital) {
          if (shareholderSections.value[0].currencySections.length > 0) {
            if (shareholderSections.value[0].currencySections[0].shareClasses.length > 0) {
              shareholderSections.value[0].currencySections[0].shareClasses[0].paidUpShareCapital = formData.paidUpShareCapital;
            }
          }
        }
        if (formData.sharesPayable) {
          if (shareholderSections.value[0].currencySections.length > 0) {
            shareholderSections.value[0].currencySections[0].sharesPayable = formData.sharesPayable;
          }
        }
        
        await loadShareholderContactData('1', formData.shareholder);
        await nextTick();
      }
    }

    // Task Flow 설정
    if (formData.taskFlow) {
      taskFlow.value = { ...taskFlow.value, ...formData.taskFlow };
    }

    console.log('✅ [SharesAllotmentForm] Form data set successfully');
    
    // 모든 watch가 완전히 처리될 때까지 약간 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // E-Filing 모드일 때 모든 패널 자동 펼치기
    if (props.eFilingMode) {
      expandAll();
    }
  } finally {
    // 데이터 복원 모드 종료
    isRestoringData.value = false;
  }
};

// initialData props가 있으면 자동으로 데이터 설정
watch(() => props.initialData, async (newData) => {
  if (newData) {
    // newData.formData가 있으면 그것을 사용, 없으면 newData 자체를 formData로 사용
    const formDataToUse = newData.formData || newData;
    if (formDataToUse) {
      await setFormData(formDataToUse);
    }
  }
}, { immediate: true });

// E-Filing 모드일 때 모든 패널 자동 펼치기
watch(() => props.eFilingMode, (isEFilingMode) => {
  if (isEFilingMode) {
    // 약간의 지연을 두어 컴포넌트가 완전히 마운트된 후 실행
    nextTick(() => {
      expandAll();
    });
  }
}, { immediate: true });

// E-Filing용 JSON 데이터 생성 함수 (async)
const getEFilingData = async () => {
  // 세션에서 Client 정보 가져오기
  const customerInfo = sessionStore.getCustomerInfo();
  const filingAgentUEN = customerInfo?.regNo || '';

  // Entity 정보 가져오기
  const selectedEntity = getEntityInfo.value;
  const entityRegNoVal = selectedEntity?.regNo || selectedEntity?.REG_NO || '';
  const entityNameVal = selectedEntity?.companyName || selectedEntity?.COMPANY_NAME || '';

  // ShareholderList 생성 (복수 Shareholder 지원)
  const shareholderList: any[] = [];

  for (const shareholderSection of shareholderSections.value) {
    if (!shareholderSection.shareholder) continue;

    const contactData = getContactData(shareholderSection.id);
    if (!contactData) continue;

    // ID Type 코드명 (B03) - BIGO1에서 E-Filing용 name 가져오기
    const idTypeItem = identificationTypeOptions.value.find(opt => opt.value === contactData.ID_TYPE_CD);
    let idTypeName = idTypeItem?.bigo1 || idTypeItem?.title || '';
    if (!idTypeName && contactData.ID_TYPE_CD) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', contactData.ID_TYPE_CD);
        idTypeName = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
      } catch (e) { console.warn('ID Type BIGO1 조회 실패:', e); }
    }

    // Nationality 코드명 (B04) - BIGO1에서 E-Filing용 name 가져오기
    const nationalityItem = nationalityOptions.value.find(opt => opt.value === contactData.NATIONALITY_CD);
    let nationalityName = nationalityItem?.bigo1 || nationalityItem?.title || '';
    if (!nationalityName && contactData.NATIONALITY_CD) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', contactData.NATIONALITY_CD);
        nationalityName = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
      } catch (e) { console.warn('Nationality BIGO1 조회 실패:', e); }
    }

    // Category 코드명 (B09) - BIGO3에서 E-Filing용 name 가져오기
    let categoryName = '';
    if (contactData.CATEGORY_CD) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', contactData.CATEGORY_CD);
        categoryName = (response as any).code?.bigo3 || (response as any).data?.code?.bigo3 || '';
      } catch (e) { console.warn('Category BIGO3 조회 실패:', e); }
    }

    // Currency 코드명 (B08)
    // - currencyName: 화면/EFiling용 (BIGO1 + CODE_NAME 등)
    // - currencyNameForApi: BizFile용 CODE_NAME 값 (예: "United States of America dollar")
    const currencyItem = currencyOptionItems.value.find(
      opt => opt.value === shareholderSection.currencySections[0]?.currency
    );
    let currencyName = currencyItem?.bigo1 || currencyItem?.title || '';
    let currencyNameForApi = '';

    if (shareholderSection.currencySections[0]?.currency) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', shareholderSection.currencySections[0].currency);
        const code = (response as any).code || (response as any).data?.code || {};
        const codeName = (code.codeName || code.CODE_NAME || '').trim();
        const bigo1 = (code.bigo1 || code.BIGO1 || '').trim();

        // currencyName은 기존 로직 유지 (BIGO1 + CODE_NAME 조합)
        if (!currencyName) {
          currencyName = bigo1 && codeName ? `${bigo1} (${codeName})` : (bigo1 || codeName);
        }

        // CurrencyName은 CODE_NAME만 사용
        currencyNameForApi = codeName;
      } catch (e) {
        console.warn('Currency CODE_NAME/BIGO1 조회 실패:', e);
      }
    }

    // Class of Shares 코드명 (A20) - CODE_NAME에서 E-Filing용 name 가져오기
    const classOfSharesItem = classOfShareOptions.value.find(opt => opt.value === shareholderSection.currencySections[0]?.shareClasses[0]?.classOfShares);
    let classOfSharesName = classOfSharesItem?.title || '';
    if (!classOfSharesName && shareholderSection.currencySections[0]?.shareClasses[0]?.classOfShares) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', shareholderSection.currencySections[0].shareClasses[0].classOfShares);
        classOfSharesName = (response as any).code?.codeName || (response as any).data?.code?.CODE_NAME || '';
      } catch (e) { console.warn('Class of Shares CODE_NAME 조회 실패:', e); }
    }

    // Mobile Country Code
    const mobileNoCountryCode = getCountryBigo3WithoutPlus(contactData.COUNTRY_CD);

    // Subclasses 생성 (모든 CurrencySection과 ShareClass를 순회)
    const subclasses: any[] = [];
    for (const currencySection of shareholderSection.currencySections) {
      for (const shareClass of currencySection.shareClasses) {
        // SubClass가 있으면만 추가
        if (shareClass.subClasses && shareClass.subClasses.length > 0) {
          for (const subClass of shareClass.subClasses) {
            const name = (subClass.name || '').trim();
            const numberOfShares = subClass.numberOfShares || '';
            // 이름과 Number of Shares 둘 다 있을 때만 포함
            if (name && numberOfShares) {
              subclasses.push({
                subclassName: name,
                numberOfShares
              });
            }
          }
        }
      }
    }

    // 첫 번째 CurrencySection과 ShareClass 가져오기
    const firstCurrencySection = shareholderSection.currencySections[0];
    const firstShareClass = firstCurrencySection?.shareClasses[0];

    // CategoryType 결정 (B09001 = Corporate, B09002 = Individual)
    const categoryType = contactData.CATEGORY_CD === 'B09001' ? 'CORP' : 'IND';

    // AddressType을 단일 문자로 변환 (B07001 = S, B07002 = F)
    const addressTypeChar = (contactData.ADDRESS_TYPE_CD || 'B07001') === 'B07001' ? 'S' : 'F';
    const contactAddressTypeChar = (contactData.ALTERNATE_ADDRESS_CD || contactData.ADDRESS_TYPE_CD || 'B07001') === 'B07001' ? 'S' : 'F';

    // SaveShareHolder 구조 생성
    const saveShareHolder = {
      // 기본 필드
      contactCd: shareholderSection.shareholder,
      contactName: contactData.CONTACT_NAME || '',
      contactEmail: contactData.CONTACT_EMAIL || '',
      shareholderName: contactData.CONTACT_NAME || '',
      uen: contactData.CONTACT_REG_NO || '',
      
      // Transaction 관련
      transactionType: 'A18001', // Allotment 코드
      transactionName: 'Allotment',
      transactionDate: (transactionDateValue.value || '').split(' ')[0] || '', // YYYY-MM-DD 형식
      
      // Category 관련
      category: {
        code: contactData.CATEGORY_CD || '',
        name: categoryName
      },
      categoryType: categoryType,
      categoryCd: contactData.CATEGORY_CD || '',
      
      // ID Type 관련
      idType: {
        code: contactData.ID_TYPE_CD || '',
        name: idTypeName
      },
      
      // Nationality 관련
      nationality: {
        code: contactData.NATIONALITY_CD || '',
        name: nationalityName
      },
      
      // Birth Date 관련
      birthDate: (contactData.DATE_OF_BIRTH || '').split(' ')[0] || '',
      hasBirthDate: !!contactData.DATE_OF_BIRTH,
      
      // Currency 관련
      currency: {
        code: firstCurrencySection?.currency || '',
        name: currencyName
      },
      currencyCd: firstCurrencySection?.currency || '',
      currencyAs: getCurrencyAs(firstCurrencySection?.currency || ''),
      currencyName: currencyNameForApi || currencyName,
      
      // Class of Shares 관련
      classOfShares: {
        code: firstShareClass?.classOfShares || '',
        name: classOfSharesName
      },
      classOfSharesAs: getClassOfSharesAs(firstShareClass?.classOfShares || ''),
      
      // Shares 관련
      numberOfShares: firstShareClass?.numberOfShares || '',
      issuedShareCapital: firstShareClass?.issuedShareCapital || '',
      paidUpShareCapital: firstShareClass?.paidUpShareCapital || '',
      considerationPaid: '0', // 기본값
      sharesPayable: getSharesPayableText(firstCurrencySection?.sharesPayable || ''),
      // Subclass가 실제로 있는 경우에만 포함
      ...(subclasses.length > 0 ? { subclasses } : {}),
      
      // Certificate 관련
      certiNo: certificateNo.value || '',
      certiType: certificateType.value || '',
      
      // Chairperson 관련
      chairpersonCd: chairPerson.value || '',
      
      // Mobile 관련
      mobileNoCountryCode: mobileNoCountryCode,
      countryCode: mobileNoCountryCode,
      mobileNo: contactData.MOBILE_NO || '',
      
      // Address 정보 (주소 타입에 따라)
      addressType: addressTypeChar, // 'S' 또는 'F'
      postalCode: (contactData.ADDRESS_TYPE_CD === 'B07001') ? (contactData.POSTAL_CODE || '') : '',
      blockNo: (contactData.ADDRESS_TYPE_CD === 'B07001') ? (contactData.BLOCK_NO || '') : '',
      blkhseNo: (contactData.ADDRESS_TYPE_CD === 'B07001') ? (contactData.BLOCK_NO || '') : '',
      streetName: (contactData.ADDRESS_TYPE_CD === 'B07001') ? (contactData.STREET_NAME || '') : '',
      buildingName: (contactData.ADDRESS_TYPE_CD === 'B07001') ? (contactData.BUILDING_NAME || '') : '',
      levelNo: (contactData.ADDRESS_TYPE_CD === 'B07001') ? (contactData.ADDR_LEVEL || '') : '',
      unitNo: (contactData.ADDRESS_TYPE_CD === 'B07001') ? (contactData.ADDR_UNIT || '') : '',
      foreignAddr1: (contactData.ADDRESS_TYPE_CD === 'B07002') ? (contactData.FOREIGN_ADDRESS_LINE_1 || '') : '',
      foreignAddr2: (contactData.ADDRESS_TYPE_CD === 'B07002') ? (contactData.FOREIGN_ADDRESS_LINE_2 || '') : '',
      
      // Contact Address (Alternate Address) 정보
      contactAddressType: contactAddressTypeChar, // 'S' 또는 'F'
      contactPostalCode: (contactData.ALTERNATE_ADDRESS_CD || contactData.ADDRESS_TYPE_CD || 'B07001') === 'B07001' ? (contactData.ALT_POSTAL_CODE || '') : '',
      contactUnitNo: (contactData.ALTERNATE_ADDRESS_CD || contactData.ADDRESS_TYPE_CD || 'B07001') === 'B07001' ? (contactData.ALT_ADDR_UNIT || '') : '',
      contactLevelNo: (contactData.ALTERNATE_ADDRESS_CD || contactData.ADDRESS_TYPE_CD || 'B07001') === 'B07001' ? (contactData.ALT_ADDR_LEVEL || '') : '',
      contactBlkhseNo: (contactData.ALTERNATE_ADDRESS_CD || contactData.ADDRESS_TYPE_CD || 'B07001') === 'B07001' ? (contactData.ALT_BLOCK_NO || '') : '',
      contactStreetName: (contactData.ALTERNATE_ADDRESS_CD || contactData.ADDRESS_TYPE_CD || 'B07001') === 'B07001' ? (contactData.ALT_STREET_NAME || '') : '',
      contactBuildingName: (contactData.ALTERNATE_ADDRESS_CD || contactData.ADDRESS_TYPE_CD || 'B07001') === 'B07001' ? (contactData.ALT_BUILDING_NAME || '') : '',
      contactForeignAddr1: (contactData.ALTERNATE_ADDRESS_CD || contactData.ADDRESS_TYPE_CD || 'B07001') === 'B07002' ? (contactData.ALT_FOREIGN_ADDRESS_LINE_1 || '') : '',
      contactForeignAddr2: (contactData.ALTERNATE_ADDRESS_CD || contactData.ADDRESS_TYPE_CD || 'B07001') === 'B07002' ? (contactData.ALT_FOREIGN_ADDRESS_LINE_2 || '') : '',
      
      // Corporate인 경우 Representative 정보
      representativeCd: contactData.REPRESENTATIVE_CD || '',
      representativeName: contactData.REPRESENTATIVE_NAME || '',
      
      // Transfer 관련 (Allotment에서는 사용 안 함)
      transferToIdx: null,
      transferToName: '',
      
      // Entity UEN (ShareholderList 항목 내부에 포함)
      entityUen: entityRegNoVal || '',
      
      // TranListIdx, TranDetailIdx는 서버에서 생성할 수도 있으므로 0으로 설정
      tranListIdx: 0,
      tranDetailIdx: 0
    };

    shareholderList.push(saveShareHolder);
  }

  // E-Filing Validation용 JSON 구조 ({ code, name } 형태)
  const eFilingDataValidation = {
    ShareholderList: shareholderList,
    EntityUEN: entityRegNoVal || '',
    FilingAgentUEN: filingAgentUEN,
    entityName: entityNameVal || ''
  };

  // E-Filing API용 JSON 구조 (name 값만)
  const eFilingDataForApi = transformCodeNameToValue(eFilingDataValidation);

  console.log('📋 [SharesAllotmentForm] getEFilingData:', {
    eFilingDataValidation,
    eFilingDataForApi,
    entityName: entityNameVal,
    shareholderListCount: shareholderList.length
  });

  return {
    eFilingDataValidation,
    eFilingDataForApi
  };
};

// 외부에서 접근 가능하도록 expose
defineExpose({
  setFormData, // E-Filing 페이지에서 TASK_DATA 설정용
  entity,
  transactionDate: transactionDateValue,
  priorApprovalFiled,
  shareholderSections,
  taskFlow,
  // 기존 인터페이스 호환 (computed)
  shareholder: computed(() => shareholderSections.value[0]?.shareholder || ''),
  currency: computed(() => shareholderSections.value[0]?.currencySections[0]?.currency || ''),
  classOfShares: computed(() => shareholderSections.value[0]?.currencySections[0]?.shareClasses[0]?.classOfShares || ''),
  numberOfShares: computed(() => shareholderSections.value[0]?.currencySections[0]?.shareClasses[0]?.numberOfShares || ''),
  issuedShareCapital: computed(() => shareholderSections.value[0]?.currencySections[0]?.shareClasses[0]?.issuedShareCapital || ''),
  paidUpShareCapital: computed(() => shareholderSections.value[0]?.currencySections[0]?.shareClasses[0]?.paidUpShareCapital || ''),
  sharesPayable: computed(() => shareholderSections.value[0]?.currencySections[0]?.sharesPayable || ''),
  getFormData,
  getFormDataWithDocVariables,
  getEFilingData,
  validateAllShareholders,
  validateAll
});
</script>

<template>
  <v-card
    variant="outlined"
    class="mb-6"
    style="border: 1px solid #e0e0e0"
  >
    <v-card-title class="text-h6 pa-4 pb-2">{{ t('startTask.Return of Allotment of Shares') || 'Return of Allotment of Shares' }}</v-card-title>
    <v-card-text class="pa-6 pt-2">
      <!-- Entity Field -->
      <div class="mb-6" style="max-width: 70%;">
        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
          {{ t('startTask.Entity') || 'Entity' }} <span class="text-error">*</span>
        </label>
        <v-autocomplete
          v-model="entity"
          :items="officerAppointmentStore.entityOptions"
          :placeholder="t('startTask.Search') || 'Search'"
          variant="outlined"
          density="compact"
          hide-details
          append-inner-icon="mdi-magnify"
          clearable
        ></v-autocomplete>
      </div>

      <!-- Transactions Date Field (공통) -->
      <div class="mb-6" style="max-width: 70%;">
        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
          {{ t('startTask.Transactions Date') || 'Transactions Date' }} <span class="text-error">*</span>
        </label>
        <v-menu
          v-model="transactionDateMenu"
          :close-on-content-click="false"
          location="bottom"
        >
          <template v-slot:activator="{ props }">
            <v-text-field
              v-bind="props"
              v-model="transactionDate"
              :placeholder="datePlaceholder"
              variant="outlined"
              density="compact"
              readonly
              prepend-inner-icon="mdi-calendar"
              data-field="transactionDate"
              :hide-details="!basicFieldValidationErrors.transactionDate"
              :error-messages="basicFieldValidationErrors.transactionDate ? [basicFieldValidationErrors.transactionDate] : []"
            ></v-text-field>
          </template>
          <v-locale-provider :locale="datePickerLocale">
            <v-date-picker
              v-model="transactionDatePickerValue"
              :first-day-of-week="firstDayOfWeek"
              @update:model-value="transactionDateMenu = false"
            ></v-date-picker>
          </v-locale-provider>
        </v-menu>
      </div>

      <!-- Task Flow Section (Entity 선택 후 표시, E-Filing 모드에서는 숨김) -->
      <TaskFlowSection
        v-if="entity && props.showTaskFlow"
        v-model="taskFlow"
        :is-all-supported="isAllSupported"
        :is-create-document-supported="isCreateDocumentSupported"
        :is-request-t-sign-supported="isRequestTSignSupported"
        :is-proceed-with-e-filing-supported="isProceedWithEFilingSupported"
        class="mb-6"
        style="max-width: 70%;"
      />

      <!-- Entity 및 Shareholder 섹션 (ControllerAppointmentForm과 동일한 패턴) -->
      <div v-if="entity" class="mb-6">
        <v-card variant="outlined" class="mb-4">
          <v-card-title class="d-flex align-center justify-space-between pa-4 pb-2">
            <div class="d-flex align-center">
              <h3 class="text-h6 ma-0">
                {{ t('startTask.Entity') || 'Entity' }}
              </h3>
            </div>
            <div class="d-flex" style="gap: 8px;">
              <a href="#" @click.prevent="expandAll" class="text-caption text-primary text-decoration-none">
                {{ t('startTask.Expand all') || 'Expand all' }}
              </a>
              <span class="text-caption">|</span>
              <a href="#" @click.prevent="collapseAll" class="text-caption text-primary text-decoration-none">
                {{ t('startTask.Collapse all') || 'Collapse all' }}
              </a>
            </div>
          </v-card-title>
          
          <v-card-text class="pa-6 pt-2">
            <v-expansion-panels
              v-model="allExpandedPanels"
              multiple
              variant="accordion"
            >
              <!-- 1. Entity 섹션 (첫 번째 expand 패널) -->
              <v-expansion-panel value="entity">
                <v-expansion-panel-title class="controller-accordion-header">
                  <div class="d-flex align-center flex-wrap">
                    <span class="font-weight-bold text-white mr-2">
                      1. Entity
                    </span>
                    <span class="text-white text-caption" v-if="getEntityInfo">
                      - {{ getEntityInfo.companyName || getEntityInfo.COMPANY_NAME || '' }}
                    </span>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div class="pa-4">
                    <!-- Entity 정보 표시 -->
                    <div v-if="getEntityInfo" class="mb-6">
                      <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">Business entity name</label>
                        <div class="text-body-1">{{ getEntityInfo.companyName || getEntityInfo.COMPANY_NAME || '-' }}</div>
                      </div>
                      
                      <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">UEN</label>
                        <div class="text-body-1">{{ getEntityInfo.regNo || getEntityInfo.REG_NO || '-' }}</div>
                      </div>
                      
                      <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">Business entity type</label>
                        <div class="text-body-1">{{ getEntityTypeName() }}</div>
                      </div>
                      
                      <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">Company type</label>
                        <div class="text-body-1">{{ getPrimaryActivityName() }}</div>
                      </div>
                    </div>

                    <!-- 라디오 버튼 질문 -->
                    <div class="mb-4">
                      <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                        Have you filed the prior approval obtained in the general meeting for the company to issue shares?
                      </label>
                      <v-radio-group
                        v-model="priorApprovalFiled"
                        inline
                        hide-details
                        density="compact"
                      >
                        <v-radio label="No" value="no" color="primary"></v-radio>
                        <v-radio label="Yes" value="yes" color="primary"></v-radio>
                      </v-radio-group>
                      <!-- No 선택 시 서비스 준비중 메시지 -->
                      <div v-if="priorApprovalFiled === 'no'" class="mt-3">
                        <div class="text-body-2 text-error">
                          Service is under preparation.
                        </div>
                      </div>
                    </div>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <!-- 2. Shareholder 섹션들 (나머지 expand 패널들) -->
              <v-expansion-panel
                v-for="(shareholderSection, shareholderIndex) in shareholderSections"
                :key="shareholderSection.id"
                :value="shareholderSection.id"
              >
                <v-expansion-panel-title 
                  :class="priorApprovalFiled === 'yes' ? 'controller-accordion-header' : 'controller-accordion-header-gray'"
                >
                  <div class="d-flex align-center flex-wrap">
                    <span :class="priorApprovalFiled === 'yes' ? 'font-weight-bold text-white mr-2' : 'font-weight-bold text-gray-dark mr-2'">
                      2. Shareholder {{ shareholderIndex + 1 }}
                    </span>
                    <span 
                      :class="priorApprovalFiled === 'yes' ? 'text-white text-caption' : 'text-gray-dark text-caption'"
                      v-if="getShareholderName(shareholderSection.shareholder)"
                    >
                      - {{ getShareholderName(shareholderSection.shareholder) }}
                    </span>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div class="pa-4">
                    <!-- Delete Shareholder Button -->
                    <div class="d-flex justify-end mb-4" v-if="shareholderSections.length > 1">
                      <v-btn 
                        icon="mdi-delete" 
                        variant="text" 
                        size="small"
                        :disabled="priorApprovalFiled === 'no' || props.eFilingMode"
                        @click="deleteShareholderSection(shareholderIndex)"
                      ></v-btn>
                    </div>

                    <!-- Shareholder Field -->
                    <div class="mb-6" style="max-width: 70%;">
              <div class="d-flex" style="gap: 12px;">
                <v-autocomplete
                  v-model="shareholderSection.shareholder"
                  :items="shareholderOptions"
                  item-title="title"
                  item-value="value"
                  :loading="isLoadingShareholders"
                  :placeholder="placeholderLabel"
                  variant="outlined"
                  density="comfortable"
                  :rules="shareholderRules"
                  :hide-details="!basicFieldValidationErrors.shareholder"
                  :error-messages="basicFieldValidationErrors.shareholder ? [basicFieldValidationErrors.shareholder] : []"
                  :disabled="!entity || priorApprovalFiled === 'no'"
                  required
                  class="required-field"
                  persistent-placeholder
                  style="flex: 1;"
                  data-field="shareholder"
                  clearable
                >
                  <template #label>
                    <span>{{ t('startTask.Shareholder') || 'Shareholder' }} <span class="required-asterisk">*</span></span>
                  </template>
                </v-autocomplete>
                <v-btn
                  color="primary"
                  variant="outlined"
                  size="small"
                  :disabled="!entity || priorApprovalFiled === 'no'"
                  @click="handleNewContact(shareholderIndex)"
                >
                  {{ t('startTask.Contact') || 'Contact' }}
                </v-btn>
              </div>
            </div>

            <!-- Contact Details (Shareholder 선택 후 표시) -->
            <template v-if="shareholderSection.shareholder && getContactData(shareholderSection.id)">
              <div class="mb-6">
                <!-- Corporate인 경우 -->
                <template v-if="isCorporateContact(shareholderSection.id)">
                  <!-- 1. Basic Corporate 섹션 -->
                  <v-card class="contact-section-card mb-4" @click="showContactDetailModal = true; currentContactDetailShareholderIndex = shareholderIndex">
                    <v-card-title class="contact-section-title">
                      {{ t('contact.Basic') || 'Basic' }}
                    </v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getJurisdictionDisplay(getContactData(shareholderSection.id)?.JURISDICTION)"
                            :label="t('contact.Jurisdiction') || 'Jurisdiction'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getContactData(shareholderSection.id)?.AUDITOR_YN === 'Yes' ? (t('common.yes') || 'Yes') : (t('common.no') || 'No')"
                            :label="t('contact.Public Accounting Entities') || 'Public Accounting Entities'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getContactData(shareholderSection.id)?.CONTACT_NAME"
                            :label="t('contact.Entity Name') || 'Entity Name'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getContactData(shareholderSection.id)?.CONTACT_REG_NO"
                            :label="t('contact.Registration Number') || 'Registration Number'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="formatDateOfIncorporation(getContactData(shareholderSection.id)?.DATE_OF_INCORPORATION)"
                            :label="t('contact.Date of Incorporation') || 'Date of Incorporation'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            prepend-inner-icon="mdi-calendar"
                            class="readonly-field"
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getRepresentativeNameDisplay(getContactData(shareholderSection.id)?.REPRESENTATIVE_CD)"
                            :label="t('contact.Representative Name') || 'Representative Name'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>

                  <!-- 2. Legal Information 섹션 -->
                  <v-card class="contact-section-card mb-4" @click="showContactDetailModal = true; currentContactDetailShareholderIndex = shareholderIndex">
                    <v-card-title class="contact-section-title">
                      {{ t('contact.Legal Information') || 'Legal Information' }}
                    </v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getContactData(shareholderSection.id)?.LEGAL_FORM"
                            :label="t('contact.Legal Form') || 'Legal Form'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getContactData(shareholderSection.id)?.INCORPORATION_STATUTE"
                            :label="t('contact.Incorporation Statute') || 'Incorporation Statute'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getContactData(shareholderSection.id)?.CORPORATE_ENTITY_REGISTER"
                            :label="t('contact.Corporate Entity Register') || 'Corporate Entity Register'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </template>

                <!-- Individual인 경우 -->
                <template v-else>
                  <!-- 1. Basic 섹션 -->
                  <v-card class="contact-section-card mb-4" @click="showContactDetailModal = true; currentContactDetailShareholderIndex = shareholderIndex">
                    <v-card-title class="contact-section-title">
                      {{ t('contact.Basic') || 'Basic' }}
                    </v-card-title>
                    <v-card-text>
                      <!-- Name (Legal Name) -->
                      <v-row>
                        <v-col cols="12">
                          <v-text-field
                            :model-value="getContactData(shareholderSection.id)?.CONTACT_NAME"
                            :label="t('contact.Name') || 'Name'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                      </v-row>
                      <!-- First Name, Middle Name, Last Name -->
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            :model-value="getContactData(shareholderSection.id)?.FIRST_NAME"
                            :label="t('contact.First Name') || 'First Name'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            :model-value="getContactData(shareholderSection.id)?.MIDDLE_NAME"
                            :label="t('contact.Middle Name') || 'Middle Name'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            :model-value="getContactData(shareholderSection.id)?.SURNAME"
                            :label="t('contact.Last Name') || 'Last Name'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                      </v-row>
                      <!-- Identification Type, Nationality/Citizenship -->
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getIdentificationTypeDisplay(getContactData(shareholderSection.id)?.ID_TYPE_CD)"
                            :label="t('contact.Identification Type') || 'Identification Type'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getNationalityDisplay(getContactData(shareholderSection.id)?.NATIONALITY_CD)"
                            :label="t('contact.Nationality/Citizenship') || 'Nationality/Citizenship'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                      </v-row>
                      <!-- Identification No., Date of Birth -->
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getContactData(shareholderSection.id)?.CONTACT_REG_NO"
                            :label="t('contact.Identification No') || 'Identification No.'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="formatDateOfBirth(getContactData(shareholderSection.id)?.DATE_OF_BIRTH)"
                            :label="t('contact.Date of Birth') || 'Date of Birth'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            prepend-inner-icon="mdi-calendar"
                            class="readonly-field"
                          />
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </template>

                <!-- 2. Residential Address 섹션 -->
                <v-card class="contact-section-card mb-4" @click="showContactDetailModal = true; currentContactDetailShareholderIndex = shareholderIndex">
                  <v-card-title class="contact-section-title">
                    {{ t('contact.Residential Address') || 'Residential Address' }}
                  </v-card-title>
                  <v-card-text>
                    <!-- Address Type -->
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          :model-value="getAddressTypeDisplay(getContactData(shareholderSection.id)?.ADDRESS_TYPE_CD)"
                          :label="t('contact.Address Type') || 'Address Type'"
                          variant="outlined"
                          density="comfortable"
                          readonly
                          hide-details
                          class="readonly-field"
                        />
                      </v-col>
                    </v-row>
                    
                    <!-- 로컬 주소인 경우: 우편번호를 활용한 주소 표시 -->
                    <template v-if="getContactData(shareholderSection.id)?.ADDRESS_TYPE_CD === 'B07001'">
                      <v-row>
                        <v-col cols="12">
                          <v-textarea
                            :model-value="formatShareholderAddress(shareholderSection.id)"
                            :label="t('contact.Residential Address') || 'Residential Address'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            rows="3"
                            :hide-details="getShareholderValidationErrors(shareholderSection.id, 'address').length === 0"
                            :error-messages="getShareholderValidationErrors(shareholderSection.id, 'address')"
                            class="readonly-field"
                            :data-section-id="shareholderSection.id"
                            data-field="address"
                          >
                            <template v-slot:label>
                              {{ t('contact.Residential Address') || 'Residential Address' }} <span class="text-error">*</span>
                            </template>
                          </v-textarea>
                        </v-col>
                      </v-row>
                    </template>
                    
                    <!-- 외국 주소인 경우 -->
                    <template v-else-if="getContactData(shareholderSection.id)?.ADDRESS_TYPE_CD === 'B07002'">
                      <v-row>
                        <v-col cols="12">
                          <v-text-field
                            :model-value="getContactData(shareholderSection.id)?.FOREIGN_ADDRESS_LINE_1"
                            :label="t('contact.Foreign Address Line 1') || 'Foreign Address Line 1'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            :hide-details="getShareholderValidationErrors(shareholderSection.id, 'foreignAddressLine1').length === 0"
                            :error-messages="getShareholderValidationErrors(shareholderSection.id, 'foreignAddressLine1')"
                            class="readonly-field"
                            :data-section-id="shareholderSection.id"
                            data-field="foreignAddressLine1"
                          >
                            <template v-slot:label>
                              {{ t('contact.Foreign Address Line 1') || 'Foreign Address Line 1' }} <span class="text-error">*</span>
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12">
                          <v-text-field
                            :model-value="getContactData(shareholderSection.id)?.FOREIGN_ADDRESS_LINE_2"
                            :label="t('contact.Foreign Address Line 2') || 'Foreign Address Line 2'"
                            variant="outlined"
                            density="comfortable"
                            readonly
                            hide-details
                            class="readonly-field"
                          />
                        </v-col>
                      </v-row>
                    </template>
                  </v-card-text>
                </v-card>

                <!-- 3. Contact 섹션 -->
                <v-card class="contact-section-card mb-4" @click="showContactDetailModal = true; currentContactDetailShareholderIndex = shareholderIndex">
                  <v-card-title class="contact-section-title">
                    {{ t('contact.Contact') || 'Contact' }}
                  </v-card-title>
                  <v-card-text>
                    <!-- Country Code, Area Code, Mobile No -->
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          :model-value="getCountryCodeDisplay(getContactData(shareholderSection.id)?.COUNTRY_CD)"
                          :label="t('contact.Country Code') || 'Country Code'"
                          variant="outlined"
                          density="comfortable"
                          readonly
                          :hide-details="getShareholderValidationErrors(shareholderSection.id, 'countryCode').length === 0"
                          :error-messages="getShareholderValidationErrors(shareholderSection.id, 'countryCode')"
                          class="readonly-field"
                          :data-section-id="shareholderSection.id"
                          data-field="countryCode"
                        >
                          <template v-slot:label>
                            {{ t('contact.Country Code') || 'Country Code' }}
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          :model-value="getContactData(shareholderSection.id)?.AREA_CODE"
                          :label="t('contact.Area Code') || 'Area Code'"
                          variant="outlined"
                          density="comfortable"
                          readonly
                          hide-details
                          class="readonly-field"
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          :model-value="getContactData(shareholderSection.id)?.MOBILE_NO"
                          :label="t('contact.Mobile No') || 'Mobile No'"
                          variant="outlined"
                          density="comfortable"
                          readonly
                          :hide-details="getShareholderValidationErrors(shareholderSection.id, 'mobileNo').length === 0"
                          :error-messages="getShareholderValidationErrors(shareholderSection.id, 'mobileNo')"
                          class="readonly-field"
                          :data-section-id="shareholderSection.id"
                          data-field="mobileNo"
                        >
                          <template v-slot:label>
                            {{ t('contact.Mobile No') || 'Mobile No' }}
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                    <!-- Email -->
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          :model-value="getContactData(shareholderSection.id)?.CONTACT_EMAIL"
                          :label="t('contact.Email') || 'Email'"
                          variant="outlined"
                          density="comfortable"
                          readonly
                          hide-details
                          class="readonly-field"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </div>
            </template>

            <!-- Currency Sections (각 Shareholder마다) -->
            <div v-for="(currencySection, currencyIndex) in shareholderSection.currencySections" :key="currencySection.id" class="currency-section mb-6">
              <v-card variant="outlined" class="mb-4">
                <v-card-title class="d-flex align-center justify-space-between pa-4 pb-2">
                  <span class="text-h6">Currency {{ currencyIndex + 1 }}</span>
                  <v-btn 
                    v-if="shareholderSection.currencySections.length > 1"
                    icon="mdi-delete" 
                    variant="text" 
                    size="small"
                    :disabled="props.eFilingMode"
                    @click="deleteCurrencySection(shareholderIndex, currencyIndex)"
                  ></v-btn>
                </v-card-title>
          
                <v-card-text class="pa-6 pt-2">
                  <!-- Currency Selection -->
                  <div class="mb-4">
                    <label class="text-subtitle-2 font-weight-medium mb-2 d-block">Currency <span class="text-error">*</span></label>
                    <v-autocomplete
                      v-model="currencySection.currency"
                      :items="currencyOptionItems"
                      item-title="title"
                      item-value="value"
                      :placeholder="placeholderLabel"
                      variant="outlined"
                      density="comfortable"
                      :rules="currencyRules"
                      :hide-details="getCurrencyValidationError(shareholderIndex, currencyIndex).length === 0"
                      :error-messages="getCurrencyValidationError(shareholderIndex, currencyIndex)"
                      clearable
                      :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no'"
                      required
                      class="required-field"
                      :data-shareholder-index="shareholderIndex"
                      :data-currency-index="currencyIndex"
                      data-field="currency"
                    ></v-autocomplete>
                  </div>

                  <!-- Shares Payable Radio Buttons -->
                  <div class="mb-4">
                    <label class="text-subtitle-2 font-weight-medium mb-2 d-block">Shares payable</label>
                    <v-radio-group v-model="currencySection.sharesPayable" inline hide-details :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no'">
                <v-radio
                  v-for="option in sharePayableOptionItems"
                  :key="option.value"
                  :label="option.title"
                  :value="option.value"
                ></v-radio>
              </v-radio-group>
            </div>

            <!-- Share Capital Section -->
            <div class="mb-4">
              <label class="text-subtitle-2 font-weight-medium mb-2 d-block">Share capital</label>
              
                    <!-- Share Classes -->
                    <div v-for="(shareClass, classIndex) in currencySection.shareClasses" :key="shareClass.id" class="share-class-section mb-4 pa-4" style="border: 1px solid #e0e0e0; border-radius: 4px;">
                      <div class="d-flex justify-space-between align-center mb-3">
                        <v-autocomplete
                          v-model="shareClass.classOfShares"
                          :items="getAvailableClassOfSharesOptions(shareholderIndex, currencyIndex, classIndex)"
                          item-title="title"
                          item-value="value"
                          placeholder="Select class of shares"
                          variant="outlined"
                          density="comfortable"
                          :rules="classOfSharesRules"
                          style="flex: 1; margin-right: 8px;"
                          :hide-details="getClassOfSharesValidationError(shareholderIndex, currencyIndex, classIndex).length === 0"
                          :error-messages="getClassOfSharesValidationError(shareholderIndex, currencyIndex, classIndex)"
                          :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no'"
                          required
                          class="required-field"
                          :data-shareholder-index="shareholderIndex"
                          :data-currency-index="currencyIndex"
                          :data-class-index="classIndex"
                          data-field="classOfShares"
                        ></v-autocomplete>
                        <v-btn
                          icon="mdi-delete"
                          variant="text"
                          size="small"
                          :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no' || props.eFilingMode"
                          @click="deleteShareClass(shareholderIndex, currencyIndex, classIndex)"
                        ></v-btn>
                      </div>

                <v-row>
                  <v-col cols="12" md="4">
                        <v-text-field
                          :ref="(el: any) => {
                            if (el) {
                              const key = getMainSharesFieldKey(shareholderIndex, currencyIndex, classIndex);
                              mainSharesFieldRefs.set(key, el);
                            }
                          }"
                          v-model="shareClass.numberOfShares"
                          label="Number of shares"
                          placeholder="e.g. 1"
                          type="number"
                          :data-shareholder-index="shareholderIndex"
                          :data-currency-index="currencyIndex"
                          :data-class-index="classIndex"
                          data-field="numberOfShares"
                          variant="outlined"
                          density="comfortable"
                          :rules="[...numberOfSharesRules, ...getMainNumberOfSharesValidationRules(shareholderIndex, currencyIndex, classIndex).value]"
                          :hide-details="getNumberOfSharesValidationError(shareholderIndex, currencyIndex, classIndex).length === 0"
                          :error-messages="getNumberOfSharesValidationError(shareholderIndex, currencyIndex, classIndex)"
                          :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no'"
                          required
                          class="required-field"
                        ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="shareClass.issuedShareCapital"
                      label="Issued share capital"
                      placeholder="e.g. 1"
                      type="number"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                      :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no'"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="shareClass.paidUpShareCapital"
                      label="Paid-up share capital"
                      placeholder="e.g. 1"
                      type="number"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                      :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no'"
                    >
                      <template #append>
                        <v-icon size="small">mdi-information-outline</v-icon>
                      </template>
                      <template #details>
                        <div class="text-caption text-medium-emphasis">Enter "0" if not applicable</div>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- Sub-classes -->
                <div v-for="(subClass, subClassIndex) in shareClass.subClasses" :key="subClass.id" class="subclass-section mt-3">
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="subClass.name"
                        label="Sub-class name"
                        placeholder="Enter sub-class name"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                        :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no'"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="d-flex align-center">
                        <v-text-field
                          :ref="(el: any) => {
                            if (el) {
                              const key = getSubClassSharesFieldKey(shareholderIndex, currencyIndex, classIndex, subClassIndex);
                              subClassSharesFieldRefs.set(key, el);
                            }
                          }"
                          v-model="subClass.numberOfShares"
                          label="Number of shares"
                          placeholder="e.g. 1"
                          type="number"
                          variant="outlined"
                          density="comfortable"
                          style="flex: 1;"
                          :rules="getSubClassValidationRules(shareholderIndex, currencyIndex, classIndex).value"
                          hide-details="auto"
                          :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no'"
                        ></v-text-field>
                        <v-btn
                          icon="mdi-delete"
                          variant="text"
                          size="small"
                          class="ml-2"
                          :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no' || props.eFilingMode"
                          @click="deleteSubClass(shareholderIndex, currencyIndex, classIndex, subClassIndex)"
                        ></v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </div>

                      <!-- Action Buttons for Sub-class and Treasury -->
                      <div class="mt-3 d-flex gap-2">
                        <v-btn
                          variant="outlined"
                          size="small"
                          prepend-icon="mdi-plus"
                          :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no' || props.eFilingMode"
                          @click="addSubClass(shareholderIndex, currencyIndex, classIndex)"
                        >
                          + Add sub-class
                        </v-btn>
                        <v-btn
                          variant="outlined"
                          size="small"
                          prepend-icon="mdi-plus"
                          :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no' || props.eFilingMode"
                          @click="addTreasuryShares(shareholderIndex, currencyIndex, classIndex)"
                        >
                          + Add treasury shares
                        </v-btn>
                      </div>
                      <!-- Treasury Shares 서비스 준비중 메시지 -->
                      <div v-if="shareClass.showTreasuryMessage" class="mt-3">
                        <div class="text-body-2 text-error">
                          Service is under preparation.
                        </div>
                      </div>
                    </div>

                    <!-- Add Another Class Button (최대 3개까지만) -->
                    <v-btn
                      v-if="currencySection.shareClasses.length < 3"
                      variant="outlined"
                      size="small"
                      prepend-icon="mdi-plus"
                      :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no' || props.eFilingMode"
                      @click="addShareClass(shareholderIndex, currencyIndex)"
                    >
                      + Add another class
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Add Currency Button (각 Shareholder 섹션 내부) -->
              <div class="d-flex justify-end mt-2 mb-4">
                <v-btn
                  variant="outlined"
                  prepend-icon="mdi-plus"
                  :disabled="!shareholderSection.shareholder || priorApprovalFiled === 'no' || props.eFilingMode"
                  @click="addCurrencySection(shareholderIndex)"
                  style="border-style: dashed;"
                >
                  + Add Currency
                </v-btn>
              </div>
            </div>
              </div>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <!-- 3. Certificate Details 패널 -->
            <v-expansion-panel value="certificate-details">
              <v-expansion-panel-title class="controller-accordion-header">
                <div class="d-flex align-center flex-wrap">
                  <span class="font-weight-bold text-white mr-2">
                    3. Certificate Details
                  </span>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="pa-4">
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="certificateType"
                        :items="certificateTypeOptions"
                        item-title="title"
                        item-value="value"
                        :label="t('entity.Certificate Type') || 'Certificate Type'"
                        variant="outlined"
                        density="comfortable"
                        :placeholder="placeholderLabel"
                        persistent-placeholder
                        hide-details
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="certificateNo"
                        :label="t('entity.Certificate No') || 'Certificate No'"
                        type="text"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="chairPerson"
                        :items="chairPersonOptions"
                        item-title="title"
                        item-value="value"
                        :label="t('entity.Chair Person') || 'Chair Person'"
                        variant="outlined"
                        density="comfortable"
                        :placeholder="placeholderLabel"
                        persistent-placeholder
                        hide-details
                        clearable
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <!-- 4. RORC Information Setting 패널 -->
            <v-expansion-panel value="rorc-info">
              <v-expansion-panel-title class="controller-accordion-header">
                <div class="d-flex align-center flex-wrap">
                  <span class="font-weight-bold text-white mr-2">
                    {{ t('startTask.RORC Information Setting') || '4. RORC Information Setting' }}
                  </span>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="pa-4">
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="representativeDirector"
                        :items="representativeDirectorOptions"
                        item-title="title"
                        item-value="value"
                        :label="t('startTask.Representative Director') || 'Representative Director'"
                        variant="outlined"
                        density="comfortable"
                        :placeholder="placeholderLabel"
                        persistent-placeholder
                        hide-details
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="rorc"
                        :items="rorcOptions"
                        item-title="title"
                        item-value="value"
                        :label="t('startTask.RORC') || 'RORC'"
                        variant="outlined"
                        density="comfortable"
                        :placeholder="placeholderLabel"
                        persistent-placeholder
                        hide-details
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="representative"
                        :items="representativeOptions"
                        item-title="title"
                        item-value="value"
                        :label="t('startTask.REPRESENTATIVE') || 'REPRESENTATIVE'"
                        variant="outlined"
                        density="comfortable"
                        :placeholder="placeholderLabel"
                        persistent-placeholder
                        hide-details
                        clearable
                        :disabled="!rorc"
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>

            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </div>

      <!-- Bottom Actions: Add Shareholder Button (현재 숨김 처리) -->
      <div class="d-flex justify-end mt-6" v-if="false && entity">
        <v-btn
          variant="outlined"
          prepend-icon="mdi-plus"
          :disabled="priorApprovalFiled === 'no' || props.eFilingMode"
          @click="addShareholderSection"
          style="border-style: dashed;"
        >
          + Add Shareholder
        </v-btn>
      </div>
    </v-card-text>
  </v-card>

  <ContactListPage
    :is-modal="true"
    v-model="showContactListModal"
    :initial-search="contactListInitialSearch"
    :on-select="(contact) => handleContactSelect(contact)"
  />

  <!-- Contact 상세 화면 모달 -->
  <v-dialog
    v-model="showContactDetailModal"
    max-width="1400px"
    width="95%"
    persistent
  >
    <v-card rounded="md">
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <span class="text-h6">{{ t('contact.Contact Detail') || 'Contact Detail' }}</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="showContactDetailModal = false"
        />
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4" style="max-height: 80vh; overflow-y: auto;">
        <ContactDetailPage
          v-if="shareholderSections[currentContactDetailShareholderIndex]?.shareholder"
          :contact-id="shareholderSections[currentContactDetailShareholderIndex].shareholder"
          :is-modal="true"
          :on-select="handleContactDetailSelect"
          @select="handleContactDetailSelect"
          @updated="handleContactUpdated"
          @back="showContactDetailModal = false"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
/* 폰트 패밀리 - Inter */
* {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* 카드 타이틀 - 18px Bold */
:deep(.v-card-title) {
  font-size: 18px !important;
  font-weight: 700 !important;
  color: #3C4650;
}

/* 레이블 - 14px */
:deep(.v-label),
:deep(.v-field-label) {
  font-size: 14px !important;
  color: #3C4650;
}

/* 입력 필드 - 14px */
:deep(.v-field__input) {
  font-size: 14px !important;
  color: #3C4650;
}

/* placeholder - 14px #777E85 */
:deep(.v-field__input::placeholder),
:deep(input::placeholder) {
  font-size: 14px !important;
  color: #777E85 !important;
}

/* 버튼 - 14px */
:deep(.v-btn) {
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: none;
}

/* Primary 버튼 */
:deep(.v-btn--color-primary) {
  background-color: #1B84FF !important;
}

.text-error {
  color: rgb(var(--v-theme-error));
}

.required-asterisk {
  color: rgb(var(--v-theme-error)) !important;
  margin-left: 2px;
}

/* Controller Accordion Header 스타일 (ControllerAppointmentForm과 동일) - 파란색 */
:deep(.controller-accordion-header) {
  background-color: #1B84FF !important;
  
  .v-expansion-panel-title {
    color: white !important;
  }
  
  .v-expansion-panel-title__icon {
    color: white !important;
  }
}

/* Controller Accordion Header 스타일 - 그레이 (priorApprovalFiled가 'no'일 때) */
:deep(.controller-accordion-header-gray) {
  background-color: #9E9E9E !important;
  
  .v-expansion-panel-title {
    color: white !important;
  }
  
  .v-expansion-panel-title__icon {
    color: white !important;
  }
}

.text-gray-dark {
  color: #424242 !important;
}

/* Contact Section Card 스타일 (OfficerAppointmentForm과 동일) */
.contact-section-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e0e0e0;
}

.contact-section-card:hover {
  border-color: #1B84FF;
  box-shadow: 0 2px 8px rgba(27, 132, 255, 0.1);
}

.contact-section-title {
  background-color: #f5f5f5;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #3C4650 !important;
}

.contact-section-card :deep(.v-card-text) {
  padding: 16px !important;
}

.readonly-field {
  pointer-events: none;
}

.readonly-field :deep(.v-field) {
  background-color: #f9f9f9;
}

.readonly-field :deep(.v-field:hover) {
  background-color: #f5f5f5;
}

.readonly-field :deep(.v-field__input) {
  color: #3C4650;
  cursor: default;
}

.readonly-field :deep(.v-field--variant-outlined) {
  border-color: #e0e0e0;
}

.readonly-field :deep(.v-field--variant-outlined:hover) {
  border-color: #e0e0e0;
}

.readonly-field :deep(.v-field__overlay) {
  background-color: transparent;
}

.readonly-field :deep(input) {
  cursor: default;
  color: #3C4650;
}

.contact-section-card :deep(.v-field__overlay) {
  background-color: transparent;
}

.contact-section-card :deep(.v-field:hover .v-field__overlay) {
  background-color: rgba(27, 132, 255, 0.05);
}
</style>
